// The Tasks page (milestone 12; module 10 U44 to U48, U51; module 04 D224, D225; drawn in
// docs/memory/modules/10-ui/screens/2026-09-19-tasks/Tasks.html). One column: add by typing, the areas as chips,
// Mine / I handed on / Everyone's I may see, the day's counts, Cobrain's suggestions, then the working directory by
// area and project with a task opening in place. Board, Calendar and Team are the other ways to look (U47).
// Nothing is ticked for the person (D60); nothing is sent (D69): Book time writes a block with no attendees.

import { ActionButton, ActionInput, ActionSelect, ActionTextarea } from '../parts/ActionButton.js';
import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, Unauthenticated, type Store } from '../api.js';
import { useLiveRefresh } from '../live.js';
import { messenger, type MessageView } from '../messenger/api.js';
import { tasks as api, type AssignablePerson, type TeamPerson, type AreaView, type Parsed, type SuggestionView, type Tally, type ProjectView, type SuggestionGroup, type TaskAct, type TaskDetail, type TaskDoing, type TaskDoingItem, type TasksView, type TasksWho, type TaskView } from './api.js';
import { dayMonth } from '../day.js';
import { masterOrder } from './order.js';
import { figuresOf, isYours, type Figures } from './figures.js';
import { agoWords, dueKind, dueWords, fromInputDate, inputDate, minutesWords, type DueKind } from './when.js';
import { FilterBar, matchesWords, tally } from '../parts/FilterBar.js';
import { useFilterParams } from '../find/params.js';
import { CompanyName, LineOrName, CompanyLogo as Logo } from '../parts/CompanyLine.js';
import { useCompanyLine } from '../company/lines.js';

export type TasksLook = 'list' | 'structure' | 'board' | 'calendar' | 'team';

/** A task with the store it lives in, so a private one is drawn violet and written to its own store (U3, D43). */
interface Row extends TaskView { store: Store; private: boolean }
interface Held { words: string; undo: (() => Promise<void>) | null; timer: number }
/** A line of the master list: a task, or a proposal drawn as one, with what `masterOrder` needs of it. */
interface Entry { r: Row; p: SuggestionView | null; dueAt: string; duePrecision: string; priority: number; proposed: boolean }

/** The filter bar's parameters in the address (pipeline F5): ?q=&company=&owner=&due=&priority=. */
const FILTERS = ['q', 'company', 'owner', 'due', 'priority'] as const;
const DUE: ReadonlyArray<readonly [DueKind, string]> = [['overdue', 'Overdue'], ['today', 'Today'], ['week', 'This week'], ['later', 'Later']];

/** The task just added from the add line, for a few seconds: its row is marked "Just added" and opened to (TK36). */
const JustAdded = createContext<string | null>(null);
const JUST_ADDED_MS = 6000;
/** "Fri 9 Oct", with the time when the words gave one: what the Added strip says the date was read as. */
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const addedWhen = (iso: string, precision: string) => {
  const d = new Date(iso);
  const day = `${WEEKDAY[d.getDay()]} ${dayMonth(d)}`;
  return precision === 'minute' ? `${day} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : day;
};

const errorWords = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e)).replace(/\.$/, '');

export function TasksPage({ stores, go, look, openId }: { stores: Store[]; go: (path: string) => void; look: TasksLook; openId?: string | undefined }) {
  const [who, setWho] = useState<TasksWho>('mine');
  const [group, setGroup] = useState<'area' | 'when'>('area');
  const [areaPick, setAreaPick] = useState<string | null>(null);
  const [views, setViews] = useState<Map<string, TasksView>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState<string | null>(openId ?? null);
  const [held, setHeld] = useState<Held | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // The task just added (TK36): marked for a few seconds, and the page scrolled to it once, where it sits by its date.
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    if (!justAdded || scrolledTo.current === justAdded) return;
    const el = [...document.querySelectorAll('[data-task]')].find((x) => x.getAttribute('data-task') === justAdded);
    if (!el) return;
    scrolledTo.current = justAdded;
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  });
  useEffect(() => {
    if (!justAdded) return;
    const t = window.setTimeout(() => setJustAdded(null), JUST_ADDED_MS);
    return () => window.clearTimeout(t);
  }, [justAdded]);
  // The filter bar (module 10 U228; pipeline F5): what the list is narrowed to, kept in the address.
  const [f, setF] = useFilterParams(FILTERS);
  const storeKey = stores.map((s) => s.tenantId).join(',');
  // Team is always everyone's the rails let the reader see, whatever My list's Whose is set to (TK43); a task opened as a
  // page is looked for there too, so a colleague's task opens from Team (TK20).
  const effectiveWho: TasksWho = look === 'team' || openId ? 'all' : who;

  const load = useCallback(async () => {
    try {
      const got = await Promise.all(stores.map((s) => api.list(s.tenantId, effectiveWho)));
      setViews(new Map(got.map((v, i) => [stores[i]!.tenantId, v])));
      setError(null);
    } catch (e) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(`The tasks could not be read: ${errorWords(e)}.`);
    } finally { setLoaded(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey, effectiveWho]);
  useEffect(() => { void load(); }, [load]);
  // Re-read when the person comes back to this page (B914). No beat: the policy in live.ts is that a read
  // nobody has timed does not get one, and this one reads every store the person has, so it is not free by
  // inspection. Somebody who times it can pass LIVE_BEAT_MS here.
  useLiveRefresh(load);
  useEffect(() => { if (openId) setOpen(openId); }, [openId]);

  const rows: Row[] = useMemo(() => stores.flatMap((s) => (views.get(s.tenantId)?.tasks ?? []).map((t) => ({ ...t, store: s, private: s.kind === 'personal' }))), [views, stores]);
  const areas = useMemo(() => stores.flatMap((s) => (views.get(s.tenantId)?.areas ?? []).map((a) => ({ ...a, store: s, private: s.kind === 'personal' }))), [views, stores]);
  const projects = useMemo(() => new Map(stores.flatMap((s) => (views.get(s.tenantId)?.projects ?? []).map((p) => [p.id, { ...p, store: s }] as const))), [views, stores]);
  const commercial = stores.find((s) => s.kind === 'commercial') ?? stores[0]!;
  const home = views.get(commercial.tenantId);

  /** Every act: optimistic where it is safe, the strip offers Undo where there is one (D62), the list re-read after. */
  const act = async (r: Row, a: TaskAct, extra: Parameters<typeof api.act>[3] = {}, words?: string, undoAct?: TaskAct) => {
    try {
      await api.act(r.store.tenantId, r.id, a, extra);
      if (words) {
        if (held) window.clearTimeout(held.timer);
        const h: Held = { words, undo: undoAct ? async () => { await api.act(r.store.tenantId, r.id, undoAct); await load(); } : null, timer: 0 };
        h.timer = window.setTimeout(() => setHeld((x) => (x === h ? null : x)), 8000);
        setHeld(h);
      }
      await load();
    } catch (e) { setSaid(`That did not go through: ${errorWords(e)}.`); }
  };

  /** Several suggestions accepted at once ("Accept all from this meeting"): each is written, the list is read once after. */
  const acceptMany = async (list: Row[]) => {
    let n = 0;
    try { for (const r of list) { await api.act(r.store.tenantId, r.id, 'accept'); n++; } }
    catch (e) { setSaid(`${n} of ${list.length} accepted; the rest did not go through: ${errorWords(e)}.`); }
    if (n > 0) setAddedWords(n === 1 ? '1 suggestion accepted' : `${n} suggestions accepted`, 'Accepted: ');
    await load();
  };

  const heading = (
    <div className="tk-top">
      <h1 className="tk-title">Tasks</h1>
      <nav className="tk-looks" aria-label="Ways to look">
        {(['list', 'structure', 'board', 'calendar', 'team'] as const).map((l) => (
          <ActionButton reading key={l} type="button" className={look === l ? 'on' : ''} aria-current={look === l ? 'page' : undefined}
                  onClick={() => go(l === 'list' ? '/tasks' : `/tasks/${l}`)}>{l === 'list' ? 'My list' : l[0]!.toUpperCase() + l.slice(1)}</ActionButton>
        ))}
      </nav>
    </div>
  );

  if (!loaded) return <div className="tasks">{heading}<p className="note">Reading your tasks…</p></div>;
  if (error && views.size === 0) return <div className="tasks">{heading}<p className="error" role="alert">{error} <ActionButton reading type="button" className="link inline" onClick={() => void load()}>Try again</ActionButton></p></div>;

  const strip = held ? (
    <div className="undo on" role="status" aria-live="polite">
      <span className="undo-words"><span>{held.words}</span></span>
      {held.undo ? <ActionButton type="button" onClick={() => { window.clearTimeout(held.timer); const h = held; setHeld(null); void h.undo?.(); }}>Undo</ActionButton> : null}
    </div>
  ) : <div className="undo" role="status" aria-live="polite" />;

  // One task opened as a page (U48 "Open as a page").
  if (openId) {
    const r = rows.find((x) => x.id === openId);
    // A proposal opened as a page (its own "Open as a page", TK20) is read before it is answered: Accept, Assign, Remove.
    const p = r ? null : stores.flatMap((st) => {
      const v = views.get(st.tenantId);
      const all = [...(v?.suggestions ?? []).flatMap((g) => g.items), ...(v?.projects ?? []).flatMap((x) => (x.pressing ? [x.pressing] : []))];
      const s = all.find((x) => x.id === openId);
      return s ? [suggestionRow(s, st)] : [];
    })[0];
    const title = r?.title ?? p?.title;
    return (
      <div className="tasks">
        <p className="note back-line"><ActionButton reading type="button" className="dotted" onClick={() => go('/tasks')}>← Tasks</ActionButton></p>
        <h1 className="sr-only">{title ?? 'A task'}</h1>
        {r ? <div className="tk-tbl"><TaskBlock r={r} projects={projects} areas={areas} open={r.id} setOpen={() => go('/tasks')} onAct={act} views={views} reload={load} go={go} say={say} showWhere asPage /></div>
           : p ? <div className="tk-tbl"><p className="tk-pagetitle"><span className="propmark">Cobrain suggests</span> {p.title}</p>
               <Opened r={p} team={views.get(p.store.tenantId)?.team ?? []} projects={projects} areas={areas} onAct={async (...a) => { await act(...a); go('/tasks'); }} reload={load} go={go} say={say} proposed asPage /></div>
           : <p className="note">This task is not in your list: it may be done, or not yours to read.</p>}
        {strip}
      </div>
    );
  }

  // The filter bar narrows what is already here: every task the list shows and every proposal come in the one read.
  // A proposal is the reader's own, so "By owner" set to anyone else hides them all.
  const projectName = (projectId: string | null) => (projectId ? projects.get(projectId)?.name ?? '' : '');
  const fits = (x: { title: string; projectId: string | null; organisation: { id: string; name: string } | null; dueAt: string; duePrecision: string; priority: number }, ownerId: string, ...words: Array<string | null>) =>
    matchesWords(f.q, x.title, projectName(x.projectId), x.organisation?.name, ...words)
    && (!f.company || x.organisation?.id === f.company)
    && (!f.owner || ownerId === f.owner)
    && (!f.due || dueKind(x.dueAt, x.duePrecision) === f.due)
    && (!f.priority || String(x.priority) === f.priority);
  // Cobrain's proposals are the reader's own to accept: they are drawn on My list and Structure only, and not under "I
  // handed on", so they are counted and offered only there too (BUGS.md TK8; TK21, Board and Team counted them).
  const proposalsDrawn = (look === 'list' || look === 'structure') && who !== 'handed';
  const allProposals = proposalsDrawn ? stores.flatMap((s) => (views.get(s.tenantId)?.suggestions ?? []).flatMap((g) => g.items.map((i) => ({ s: i, store: s })))) : [];
  const narrowedRows = rows.filter((r) => fits(r, r.ownerId, r.notes, r.ownerName, r.delegateName));
  const proposals = allProposals.filter(({ s, store }) => fits(s, views.get(store.tenantId)?.me ?? '', s.cue, s.saidBy));
  const shown = (list: Row[]) => areaPick === 'unfiled' ? list.filter((r) => unfiledIn(projects, r)) : areaPick ? list.filter((r) => r.projectId && projects.get(r.projectId)?.areaId === areaPick) : list;
  // The master list's order is worked out here so that the counts line and the list itself are the same
  // reading. The store's own tally counts accepted tasks only, so on 2026-09-20 it read "1 overdue" above
  // nine overdue proposals; counting the folded group in instead read "115 overdue" above a list of 21.
  // My list (Mike, 7 Oct, "Yours first", module 10 U374; BUGS.md TK38): the reader's own tasks in the master order, and
  // Cobrain's proposals in that order apart from them, in one fold beneath. D239's Accept step is unchanged.
  // A parked task leaves My list and waits in the Board's Parked lane, and stays in the open and overdue figures (Mike by
  // pop-up, 8 Oct 2026, "Off My list, still counted (Recommended)"; U376; BUGS.md TK50).
  // A task handed on and parked (Leave it with, TK9) keeps the status 'delegated' (migration 221) and carries its park's date
  // (BUGS.md TK52): it is parked, so it leaves My list too.
  const onMyList = (r: Row) => r.status !== 'done' && r.status !== 'parked' && !r.parkedUntil;
  const master = masterOrder(shown(narrowedRows).filter(onMyList)
    .map((r) => ({ r, p: null as SuggestionView | null, dueAt: r.dueAt, duePrecision: r.duePrecision, priority: r.priority, proposed: false })));
  const proposedOrder = masterOrder(proposals.map(({ s, store }) => ({ r: suggestionRow(s, store), p: s as SuggestionView | null, dueAt: s.dueAt, duePrecision: s.duePrecision, priority: s.priority, proposed: true })));
  const proposalTotal = proposalsDrawn ? stores.reduce((n, s) => n + (views.get(s.tenantId)?.suggestionTotal ?? 0), 0) : 0;
  // One set of figures on every look (TK40, TK21, TK23): the reader's own open tasks among the rows the page read for
  // Whose and the filter bar, never the proposals, and never one look's own reading of them.
  const meOf = (r: Row) => views.get(r.store.tenantId)?.me ?? '';
  const figures = figuresOf(narrowedRows, meOf);

  // What the bar counts and offers: the open tasks of the list (Whose and the area as set) and the proposals.
  // On My list the bar counts what the list draws, so a parked task is not among them there (TK50); Structure draws it.
  const openAll = shown(rows).filter((r) => (look === 'list' ? onMyList(r) : r.status !== 'done'));
  const filtering = FILTERS.some((k) => Boolean(f[k]));
  const baseCount = openAll.length + allProposals.length;
  const shownCount = openAll.filter((r) => narrowedRows.includes(r)).length + proposals.length;
  // The Board draws the reader's own tasks, each lane by its state; Team draws everyone's the rails let them read.
  const boardAll = rows.filter((r) => isYours(r, meOf(r)));
  const teamAll = rows.filter((r) => r.status !== 'done' && r.store.kind === 'commercial');
  // The bar says how many a filter leaves only while one is set; the figures line says the rest (the drawing, U374).
  const [barShown, barTotal] = !filtering ? [null, null]
    : look === 'board' ? [boardAll.filter((r) => narrowedRows.includes(r)).length, boardAll.length]
    : look === 'team' ? [teamAll.filter((r) => narrowedRows.includes(r)).length, teamAll.length]
    : [shownCount, baseCount];
  const offered = [...openAll.map((r) => ({ x: r as { organisation: Row['organisation']; dueAt: string; duePrecision: string; priority: number }, owner: { id: r.ownerId, name: r.ownerName ?? 'Someone' } })),
    ...allProposals.map(({ s, store }) => ({ x: s, owner: { id: views.get(store.tenantId)?.me ?? '', name: 'You' } }))];
  const ownerName = (o: { id: string; name: string }) => (stores.some((st) => views.get(st.tenantId)?.me === o.id) ? { id: o.id, name: 'You' } : o);
  const bar = (
    <FilterBar find="Find a task" q={f.q} onFind={(q) => setF({ q })} shown={barShown} total={barTotal} noun={['task', 'tasks']}
      onClearAll={() => setF({ q: '', company: '', owner: '', due: '', priority: '' })}
      picks={[
        { label: 'By company', find: 'Find a company', any: 'Any company', value: f.company, options: tally(offered, (o) => o.x.organisation), onChange: (company) => setF({ company }) },
        { label: 'By owner', find: 'Find a person', any: 'Anyone', value: f.owner, options: tally(offered, (o) => ownerName(o.owner)), onChange: (owner) => setF({ owner }) },
        { label: 'Due', find: 'When it is due', any: 'Any time', value: f.due, options: DUE.map(([id, name]) => ({ id, name, n: offered.filter((o) => dueKind(o.x.dueAt, o.x.duePrecision) === id).length })), onChange: (due) => setF({ due }) },
        { label: 'Priority', find: 'Find a priority', any: 'Any priority', value: f.priority, options: [1, 2, 3, 4].map((p) => ({ id: String(p), name: priorityWords(p), n: offered.filter((o) => o.x.priority === p).length })), onChange: (priority) => setF({ priority }) },
      ]} />
  );

  const onQuiet = () => {
    const q = rows.filter((r) => r.mine && r.delegateId && quietFor(r) !== null && r.status !== 'done').sort((a, b) => (b.quietDays ?? 0) - (a.quietDays ?? 0))[0];
    if (q) { setGroup('area'); setAreaPick(null); setOpen(q.id); window.setTimeout(() => document.querySelector(`[data-task="${CSS.escape(q.id)}"]`)?.scrollIntoView({ block: 'center' }), 60); }
  };
  const quiet = [...views.values()].map((v) => v.counts.quiet).find((q) => q) ?? null;
  const counts = <Counts figures={figures} quiet={quiet} onQuiet={onQuiet}
    of={look === 'team' ? 'everyone\'s tasks you may see'
      : `${figures.open} of yours open${proposalTotal > 0 && (look === 'list' || look === 'structure') ? `, ${proposalTotal.toLocaleString('en-GB')} suggested${look === 'list' ? ' below' : ''}` : ''}`} />;

  return (
    <JustAdded.Provider value={justAdded}>
    <div className="tasks">
      {heading}
      {said ? <p className="error" role="alert">{said} <ActionButton reading type="button" className="link inline" onClick={() => setSaid(null)}>Dismiss</ActionButton></p> : null}
      {look === 'list' || look === 'structure' ? (
        <>
          <AddLine store={commercial} onAdded={async (t) => {
            setSaid(null); await load();
            setAddedWords(`${t.title}, due ${addedWhen(t.dueAt, t.duePrecision)}`);
            scrolledTo.current = null; setJustAdded(t.id);
          }} />
          {look === 'list'
            ? <AutoBar store={commercial} settings={home?.settings ?? { autoTasksOn: true, detail: 2 }} reload={load} />
            : <AreaStrip areas={areas} projects={projects} rows={rows} pick={areaPick} onPick={setAreaPick} store={commercial} reload={load} />}
          <div className="tk-filters">
            <div className="tk-chips" role="group" aria-label="Whose">
              {([['mine', 'Mine'], ['handed', 'I handed on'], ['all', 'Everyone\'s I may see']] as const).map(([k, w]) => (
                <ActionButton reading key={k} type="button" className={who === k ? 'chip on' : 'chip'} aria-pressed={who === k} onClick={() => setWho(k)}>{w}</ActionButton>
              ))}
            </div>
            {look === 'list'
              ? <div className="tk-group"><span>Everything you have open: what you have just missed, then today, then the week ahead</span></div>
              : (
                <div className="tk-group" role="group" aria-label="Group by">
                  <span>Group by</span>
                  <ActionButton reading type="button" className={group === 'area' ? 'seg on' : 'seg'} aria-pressed={group === 'area'} onClick={() => setGroup('area')}>Area</ActionButton>
                  <ActionButton reading type="button" className={group === 'when' ? 'seg on' : 'seg'} aria-pressed={group === 'when'} onClick={() => setGroup('when')}>When</ActionButton>
                </div>
              )}
          </div>
          {bar}
          {look === 'list' && who === 'mine' ? <h2 className="tk-yours">Yours</h2> : null}
          {counts}
          {look === 'list' ? (
            <MasterList order={master} proposed={[...proposedOrder.list, ...proposedOrder.long]} done={shown(narrowedRows).filter((r) => r.status === 'done')}
                        proposalTotal={proposalTotal} filtering={filtering}
                        projects={projects} areas={areas} open={open} setOpen={setOpen} onAct={act} views={views} reload={load} go={go} say={say} team={home?.team ?? []} />
          ) : (
            <>
              {group === 'area'
                ? <Directory rows={shown(narrowedRows)} areas={areaPick ? areas.filter((a) => a.id === areaPick) : areas} projects={projects} open={open} setOpen={setOpen} onAct={act} views={views} reload={load} go={go} say={say} />
                : <ByWhen rows={shown(narrowedRows)} projects={projects} areas={areas} open={open} setOpen={setOpen} onAct={act} views={views} reload={load} go={go} say={say} />}
              {/* Cobrain's suggestions fold below the areas (Mike, 7 Oct: "Below your projects", "Yes, below"; U376). */}
              <Suggestions groups={proposalsDrawn ? stores.flatMap((s) => (views.get(s.tenantId)?.suggestions ?? []).map((g) => ({ g, store: s }))) : []} projects={projects} areas={areas}
                           total={stores.reduce((n, s) => n + (views.get(s.tenantId)?.suggestionTotal ?? 0), 0)} areaPick={areaPick}
                           team={home?.team ?? []} onAct={act} onAcceptMany={acceptMany} />
            </>
          )}
        </>
      ) : look === 'board' ? <>{bar}{counts}<Board rows={boardAll.filter((r) => narrowedRows.includes(r))} projects={projects} areas={areas} onAct={act} go={go} /></>
        : look === 'calendar' ? <Calendar rows={rows} store={commercial} />
        : <>{bar}{counts}<Team rows={narrowedRows} projects={projects} areas={areas} me={home?.me ?? ''} team={home?.team ?? []} go={go} /></>}
      {strip}
    </div>
    </JustAdded.Provider>
  );

  /** One line in the strip for something that has no Undo of its own (a project put away, a merge). */
  function say(words: string) {
    if (held) window.clearTimeout(held.timer);
    const h: Held = { words, undo: null, timer: 0 };
    h.timer = window.setTimeout(() => setHeld((x) => (x === h ? null : x)), 6000);
    setHeld(h);
  }

  function setAddedWords(title: string, lead = 'Added: ') {
    if (held) window.clearTimeout(held.timer);
    const h: Held = { words: lead === 'Accepted: ' ? `${title}: they are in your list.` : `${lead}${title}.`, undo: null, timer: 0 };
    h.timer = window.setTimeout(() => setHeld((x) => (x === h ? null : x)), 5000);
    setHeld(h);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// The add line: typed as it would be said; what the words did not give is asked in the same line.
// ---------------------------------------------------------------------------------------------------------------
function AddLine({ store, onAdded }: { store: Store; onAdded: (t: { id: string; title: string; dueAt: string; duePrecision: string }) => Promise<void> }) {
  const [text, setText] = useState('');
  // Set only when the words gave no date: the line then asks for one, and the next Enter saves (TK36, U374).
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [due, setDue] = useState('');
  const [priority, setPriority] = useState(3);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  // Held from the first Enter until the save answers, so a second Enter or press adds nothing. A state flag alone does
  // not do it: two Enters in one tick both read it before React has re-rendered.
  const lock = useRef(false);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === 'q' || e.key === 'Q') { e.preventDefault(); ref.current?.focus(); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, []);

  /** One press: read the words, and save at once when they give a date; when they give none, ask for it. */
  const submit = async () => {
    if (lock.current) return;
    if (!parsed && !text.trim()) return;
    lock.current = true; setBusy(true); setErr(null);
    try {
      let p = parsed;
      let dueAt: string | null; let duePrecision: string;
      if (p) { dueAt = fromInputDate(due); duePrecision = 'day'; }
      else {
        p = await api.parse(store.tenantId, text);
        if (!p.dueAt) { setParsed(p); setDue(''); return; }
        dueAt = p.dueAt; duePrecision = p.duePrecision ?? 'day';
      }
      if (!dueAt) return;   // the line already asks for the date
      // The company the words named goes with the task; the project the reader guessed from it does not, so the server
      // files it by rule, as Cobrain's filing, and never as the person's own choice (TK39).
      const { id } = await api.add(store.tenantId, { title: p.title, dueAt, duePrecision, priority, organisationId: p.company?.id ?? null });
      setText(''); setParsed(null); setDue(''); setPriority(3);
      await onAdded({ id, title: p.title, dueAt, duePrecision });
    } catch (e) { setErr(`Not added: ${errorWords(e)}. Your words are still in the line.`); }
    finally { lock.current = false; setBusy(false); }
  };

  return (
    <form className={`tk-add${parsed ? ' on' : ''}${busy ? ' busy' : ''}`} aria-busy={busy} onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <label className="tk-add-line">
        <span className="plus" aria-hidden="true">+</span>
        <span className="sr-only">Add a task</span>
        <ActionInput ref={ref} id="tk-add" value={text} readOnly={busy} onChange={(e) => { setText(e.target.value); setParsed(null); setErr(null); }} placeholder={'Add a task, as you\'d say it: "send Kambi the rate card Friday"'} autoComplete="off" />
        {busy ? <span className="tk-adding" role="status">Adding it</span> : <kbd aria-hidden="true" title="Press Q anywhere on the page to jump here">Q</kbd>}
      </label>
      {parsed ? (
        <div className="tk-add-read">
          <span className="tk-ask">The words give no date: when is it due?</span>
          <label>Due <ActionInput type="date" value={due} autoFocus onChange={(e) => setDue(e.target.value)} required aria-invalid={!due} /></label>
          <label>Priority <ActionSelect value={priority} onChange={(e) => setPriority(Number(e.target.value))}>{[1, 2, 3, 4].map((p) => <option key={p} value={p}>{priorityWords(p)}</option>)}</ActionSelect></label>
          <ActionButton type="submit" className="opt lead" disabled={busy || !due}>Add it</ActionButton>
          <ActionButton type="button" className="opt" onClick={() => { setParsed(null); ref.current?.focus(); }}>Change the words</ActionButton>
        </div>
      ) : null}
      {err ? <p className="error tk-add-err" role="alert">{err} <ActionButton type="submit" className="opt small" disabled={busy}>Try again</ActionButton></p> : null}
    </form>
  );
}

/**
 * Not yet filed (BUGS.md TK28): filed nowhere, or in a project the page does not draw, which is one put away or merged
 * while it held a colleague's task (only the reader's own move; work.task's update policy keeps a colleague's). Structure,
 * the chip, its count and its filter all read this one test, so the task is never counted in one and missing in another.
 */
function unfiledIn(projects: Map<string, unknown>, r: { projectId: string | null }): boolean {
  return !r.projectId || !projects.has(r.projectId);
}

// ---------------------------------------------------------------------------------------------------------------
// The areas as chips, with open counts; private ones violet and only with Personal on (they come from that store).
// ---------------------------------------------------------------------------------------------------------------
function AreaStrip({ areas, projects, rows, pick, onPick, store, reload }: {
  areas: Array<AreaView & { store: Store; private: boolean }>; projects: Map<string, ProjectView & { store: Store }>; rows: Row[]; pick: string | null; onPick: (id: string | null) => void; store: Store; reload: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const open = rows.filter((r) => r.status !== 'done').length;
  // Suggestions waiting in an area are counted beside its open tasks (a chip reading 0 over 155 suggestions said "empty").
  const suggestedIn = (areaId: string) => [...projects.values()].reduce((n, p) => n + (p.areaId === areaId ? p.suggested : 0), 0);
  const withWork = areas;   // every area, always (the drawing's strip), private ones only with Personal on
  return (
    <div className="tk-strip" role="group" aria-label="Areas">
      <ActionButton reading type="button" className={pick === null ? 'chip on' : 'chip'} aria-pressed={pick === null} onClick={() => onPick(null)}>Everything <b>{open}</b></ActionButton>
      {withWork.map((a) => (
        <ActionButton reading key={a.id} type="button" className={`chip${a.private ? ' pv' : ''}${pick === a.id ? ' on' : ''}${a.madeBy === 'cobrain' && !a.kept ? ' cobrain' : ''}`} aria-pressed={pick === a.id} onClick={() => onPick(pick === a.id ? null : a.id)}
                title={a.madeBy === 'cobrain' && !a.kept ? 'Made by Cobrain: keep, rename or merge it' : suggestedIn(a.id) ? `${a.open} open, ${suggestedIn(a.id)} suggested` : undefined}>
          {a.name} <b>{a.open}</b>{suggestedIn(a.id) ? <b className="sg" aria-label={`, ${suggestedIn(a.id)} suggested`}>+{suggestedIn(a.id)}</b> : null}
        </ActionButton>
      ))}
      {rows.some((r) => r.status !== 'done' && unfiledIn(projects, r)) ? (
        <ActionButton reading type="button" className={pick === 'unfiled' ? 'chip on' : 'chip'} aria-pressed={pick === 'unfiled'} onClick={() => onPick(pick === 'unfiled' ? null : 'unfiled')}>Not yet filed <b>{rows.filter((r) => r.status !== 'done' && unfiledIn(projects, r)).length}</b></ActionButton>
      ) : null}
      {adding ? (
        <form className="tk-new" onSubmit={async (e) => { e.preventDefault(); if (!name.trim()) return; await api.addArea(store.tenantId, name.trim()); setName(''); setAdding(false); await reload(); }}>
          <label className="sr-only" htmlFor="tk-new-area">New area</label>
          <ActionInput id="tk-new-area" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Name the area" />
          <ActionButton type="submit" className="opt small">Add</ActionButton>
          <ActionButton reading type="button" className="opt small" onClick={() => setAdding(false)}>Cancel</ActionButton>
        </form>
      ) : <ActionButton type="button" className="chip ghost" onClick={() => setAdding(true)}>+ New area</ActionButton>}
    </div>
  );
}

/** The day's figures, the same on every look (figures.ts), and what they are of: the reader's own open tasks. */
/**
 * A hand-on's quiet days, when the page marks it quiet (BUGS.md TK26): only once Cobrain's quiet warning has fired on it
 * and nothing has been heard since (D68: silence since the hand-on and a due date within the warning's window), never on
 * the days alone. Null when it is not to be marked.
 */
const quietFor = (r: { quietWarned?: boolean; quietDays: number | null }): number | null => (r.quietWarned && r.quietDays !== null ? r.quietDays : null);

/** ", due Fri 9" from the hand-on's own due date (BUGS.md TK10), nothing from an API that sends none. */
const dueSaid = (at: string | null | undefined): string => (at ? `, due ${dueWords(at, 'day').replace(/^(Today|Tomorrow)/, (w) => w.toLowerCase())}` : '');

function Counts({ figures, of, quiet, onQuiet }: { figures: Figures; of: string; quiet: TasksView['counts']['quiet']; onQuiet: () => void }) {
  return (
    <p className="tk-today">
      <span className={figures.overdue ? 'w' : undefined}><b className="big">{figures.overdue}</b> overdue</span>
      <span><b className="big">{figures.today}</b> due today</span>
      <span><b className="big">{figures.withOthers}</b> with others</span>
      <span className="of">{of}</span>
      {quiet ? <ActionButton reading type="button" className="warnc tk-quiet" onClick={onQuiet}>{quiet.count === 1 && quiet.name ? `Your hand-on to ${quiet.name} has been quiet ${quiet.days} days${dueSaid(quiet.dueAt)}: open it` : `${quiet.count} hand-ons quiet, the longest ${quiet.days} days${dueSaid(quiet.dueAt)}: open it`}</ActionButton> : null}
    </p>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// From Cobrain (U45): suggestions grouped by where they came from; Accept, Assign, Remove in one click (U298).
// ---------------------------------------------------------------------------------------------------------------
function Suggestions({ groups: all, projects, areas, team, onAct, onAcceptMany, total: whole, areaPick }: {
  total: number; groups: Array<{ g: SuggestionGroup; store: Store }>; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>;
  team: TeamPerson[]; areaPick: string | null;
  onAct: (r: Row, a: TaskAct, extra?: Parameters<typeof api.act>[3], words?: string, undo?: TaskAct) => Promise<void>;
  onAcceptMany: (list: Row[]) => Promise<void>;
}) {
  const [more, setMore] = useState(false);
  const [opened, setOpened] = useState(false);
  const [tallies, setTallies] = useState<Record<string, Tally>>({});
  // An area chip narrows the suggestions too: those filed in that area, or with "Not yet filed" those filed nowhere.
  const inPick = (s: SuggestionView) => areaPick === null ? true : areaPick === 'unfiled' ? unfiledIn(projects, s) : !!s.projectId && projects.get(s.projectId)?.areaId === areaPick;
  const groups = all.map(({ g, store }) => ({ g: { ...g, items: g.items.filter(inPick) }, store })).filter((x) => x.g.items.length > 0);
  const total = groups.reduce((n, x) => n + x.g.items.length, 0);
  // U45's line on each source (how many actions, how many already yours, how many done), read after the page draws.
  // A chat's group is one day of it, which the tally (by thread) cannot count, so it has none.
  const firstKeys = groups.filter((x) => x.g.kind && !x.g.key.includes(':')).slice(0, 8).map((x) => `${x.store.tenantId}|${x.g.key}`).join(',');
  useEffect(() => {
    const byStore = new Map<string, string[]>();
    for (const k of firstKeys ? firstKeys.split(',') : []) { const [st, key] = k.split('|'); byStore.set(st!, [...(byStore.get(st!) ?? []), key!]); }
    for (const [st, keys] of byStore) api.tallies(st, keys).then((r) => setTallies((t) => ({ ...t, ...r.tallies }))).catch(() => undefined);
  }, [firstKeys]);
  if (total === 0) return null;
  const FIRST = 5;
  let shownCount = 0;
  const n = areaPick ? total : whole;
  return (
    <section className="tk-fold tk-sugfold" aria-label="Suggested by Cobrain">
      <div className="tk-foldh">
        <div><h2><span className="n">{n.toLocaleString('en-GB')}</span> suggested by Cobrain{areaPick ? ' in this area' : ''}</h2>
          <p className="c">By where they came from, the most pressing first.</p></div>
        <ActionButton reading type="button" className="opt" aria-expanded={opened} onClick={() => setOpened(!opened)}>{opened ? 'Hide them again' : `Show the ${n.toLocaleString('en-GB')}`}</ActionButton>
      </div>
      {opened ? <div className="tk-tbl sugg">
        {groups.map(({ g, store }) => {
          const items = more ? g.items : g.items.filter(() => shownCount++ < FIRST);
          if (items.length === 0) return null;
          const yours = g.items.filter((i) => !i.theirs);
          const t = tallies[g.key];
          return (
            <Fragment key={`${store.tenantId}:${g.key}`}>
              <div className="tk-src"><b>{g.title}</b>{g.at ? <span>{dueWords(g.at, 'day')}</span> : null}
                {t && t.total > 1 ? <span>{t.total} actions, {t.yours} already yours{t.done ? `, ${t.done} done` : ''}</span> : null}
                {yours.length > 1 && yours.length <= 20 ? <ActionButton type="button" className="link inline" onClick={() => void onAcceptMany(yours.map((i) => suggestionRow(i, store)))}>Accept all {yours.length}</ActionButton> : null}
              </div>
              {items.map((s) => <SuggestionLine key={s.id} s={s} store={store} projects={projects} areas={areas} team={team} onAct={onAct} />)}
            </Fragment>
          );
        })}
        {total > FIRST ? <ActionButton reading type="button" className="tk-more" aria-expanded={more} onClick={() => setMore(!more)}>{more ? 'Show fewer' : `${total - FIRST} more, grouped by where they came from. Show`}</ActionButton> : null}
      </div> : null}
    </section>
  );
}

/** A suggestion as a row the page can act on (it is the person's only once accepted, D60). */
function suggestionRow(s: SuggestionView, store: Store): Row {
  return {
    id: s.id, title: s.title, status: 'suggested', mine: true, ownerId: '', ownerName: null, delegateId: null, delegateName: null, quietDays: null, quietWarned: false,
    dueAt: s.dueAt, duePrecision: s.duePrecision, priority: s.priority, projectId: s.projectId, filedBy: null, filing: s.filing, dueSource: 'cobrain', prioritySource: 'cobrain', notes: null, minutesNeeded: null, booked: null,
    steps: { done: 0, total: 0 }, chatId: null, chatMessages: 0, matterId: null, createdFrom: 'inferred', doneAt: null, doneBy: null, parkedUntil: null, doneSource: null, doneByMe: false, organisation: s.organisation,
    asked: [], askedBy: null, store, private: store.kind === 'personal',
  };
}

/** One suggestion, the same in From Cobrain, under a project and in the master list: whose it is, the words behind it,
 *  when, and the choices. The person's own leads with Accept, then Assign and Remove; someone else's commitment leads
 *  with leaving it with them, then Track it and Remove (U298, Mike on 5 Oct: "Default options should be Accept, Assign,
 *  Remove"). Give to… is gone from the page: Assign asks, and the task stays the reader's until it is taken. */
function SuggestionLine({ s, store, projects, areas, onAct, inDir, onGone, inMaster, open, onToggle }: {
  s: SuggestionView; store: Store; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>;
  team?: TeamPerson[]; inDir?: boolean; onGone?: () => void;
  /** In the master list a proposal is drawn as a task is (D239: in its place in the order, marked), with a dashed
   *  tick it cannot tick, the filing under the title and the priority figure in its column. */
  inMaster?: boolean;
  /** In the master list a proposal opens in place, like a task (U93). */
  open?: boolean; onToggle?: () => void;
  onAct: (r: Row, a: TaskAct, extra?: Parameters<typeof api.act>[3], words?: string, undo?: TaskAct) => Promise<void>;
}) {
  const [assigning, setAssigning] = useState(false);
  const r = suggestionRow(s, store);
  const p = s.projectId ? projects.get(s.projectId) : undefined;
  const a = p ? areas.find((x) => x.id === p.areaId) : undefined;
  const kind = dueKind(s.dueAt, s.duePrecision);
  const theirs = s.theirs && s.saidBy;
  const first = s.saidBy?.split(' ')[0] ?? '';
  const act = (x: TaskAct, extra: Parameters<typeof api.act>[3], words: string, undo?: TaskAct) => { onGone?.(); void onAct(r, x, extra, words, undo); };
  /** Mike, on the two answers he had: "it is, it is just not that important." Setting the figure says both at
   *  once: the accept, then the priority, which migration 136's trigger stamps as his so nothing takes it back. */
  const acceptAt = (at: number) => {
    onGone?.();
    void onAct(r, 'accept', { priority: at }, at === 3 ? 'Accepted: it is in your list.' : `Accepted at ${priorityWords(at)}.`);
  };
  return (
    <>
    <div className={`tk-row sugg${inMaster ? ' prop master' : ''}${open ? ' open' : ''}${inDir ? ' in-dir' : ''}${store.kind === 'personal' ? ' pv' : ''}`} data-task={s.id}>
      {inMaster ? <span className="tick prop" aria-hidden="true" title="Cobrain suggested this; accept it and it becomes a tick" /> : null}
      {inMaster ? (
        <ActionButton reading type="button" className="t" aria-expanded={open ?? false} onClick={() => onToggle?.()}>
          <span className="tt">{s.title}</span>
        <span className="sub">
          {inMaster ? <span className="propmark">Cobrain suggests</span> : null}
          {inDir ? null : a && p ? <span className="filed"><b>{a.name}</b> › {p.name}</span> : <span className="filed"><span className="unfiled">Not yet filed</span></span>}
          {s.cue ? <span className="cue">{s.said === 'you' ? 'you said ' : s.said === 'them' && s.saidBy ? `${s.saidBy}: ` : ''}“{s.cue}”</span> : null}
        </span>
        </ActionButton>
      ) : (
      <div className="t"><span className="tt">{s.title}</span>
        <span className="sub">
          {inDir ? null : a && p ? <span className="filed"><b>{a.name}</b> › {p.name}</span> : <span className="filed"><span className="unfiled">Not yet filed</span></span>}
          {s.cue ? <span className="cue">{s.said === 'you' ? 'you said ' : s.said === 'them' && s.saidBy ? `${s.saidBy}: ` : ''}“{s.cue}”</span> : null}
        </span></div>)}
      {theirs && !inMaster ? <span className="person">{first}'s</span>
        : <span className={kind === 'overdue' || kind === 'today' ? 'plate' : 'plate quiet'}
                aria-label={`${kind === 'overdue' ? 'Was due' : 'Due'} ${dueWords(s.dueAt, s.duePrecision)}`}>
            {kind === 'overdue' || kind === 'today' ? <i aria-hidden="true" /> : null}
            {inMaster ? dueWords(s.dueAt, s.duePrecision) : `${kind === 'overdue' ? 'was due' : 'due'} ${dueWords(s.dueAt, s.duePrecision)}`}
          </span>}
      <div className="opts">
        {theirs ? (
          <>
            <ActionButton type="button" className="opt lead" onClick={() => act('leave_with', {}, `Left with ${s.saidBy}.`)}>Leave with {first}</ActionButton>
            <ActionButton type="button" className="opt" onClick={() => act('track', {}, 'Tracked: it is in your list.')}>Track it</ActionButton>
          </>
        ) : (
          <>
            <ActionButton type="button" className="opt lead" onClick={() => act('accept', {}, 'Accepted: it is in your list.')}>Accept</ActionButton>
            {/* A personal store has one person in it, so there is nobody to assign to. */}
            {store.kind === 'personal' ? null
              : <ActionButton type="button" className={assigning ? 'opt on' : 'opt'} aria-expanded={assigning} onClick={() => setAssigning(!assigning)}>Assign</ActionButton>}
          </>
        )}
        <ActionButton type="button" className="opt" onClick={() => act('not_a_task', {}, `Removed: ${s.title}. Cobrain will learn from it.`, 'restore')}>Remove</ActionButton>
      </div>
      {inMaster ? (
        <ActionSelect className={s.priority === 1 ? 'pri p1' : 'pri'} value={s.priority}
                aria-label={`Priority ${s.priority} of 4 for ${s.title}. Setting it accepts this as your task at that weight`}
                title={`${priorityWords(s.priority)}. Setting it accepts this as yours at that weight`}
                onChange={(e) => acceptAt(Number(e.target.value))}>
          {[1, 2, 3, 4].map((x) => <option key={x} value={x}>{x}</option>)}
        </ActionSelect>
      ) : null}
    </div>
    {assigning ? <AssignPick r={r} onCancel={() => setAssigning(false)} onPick={(p) => { setAssigning(false); act('assign', { to: p.principalId }, assignedWords(p, true), 'withdraw'); }} /> : null}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// The directory (U48): area, then company or project, then its tasks.
// ---------------------------------------------------------------------------------------------------------------
type Common = {
  projects: Map<string, ProjectView & { store: Store }>; open: string | null; setOpen: (id: string | null) => void;
  /** Say what just happened in the page's own strip. A project that has been put away cannot say it itself. */
  say: (words: string) => void;
  onAct: (r: Row, a: TaskAct, extra?: Parameters<typeof api.act>[3], words?: string, undo?: TaskAct) => Promise<void>;
  views: Map<string, TasksView>; reload: () => Promise<void>; go: (path: string) => void;
};

/**
 * What a person may do to an area (U102, and the 2026-09-19 decision that people create, rename, merge and move):
 * rename it, or put it away. Not on the heading of every area as buttons — it opens from one word, because the
 * directory is a reading surface first and 165 rows of controls is the clutter Mike refused.
 */
function AreaControls({ a, empty, reload, say }: { a: AreaView & { store: Store }; empty: boolean; reload: () => Promise<void>; say: (words: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(a.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await reload(); setOpen(false); }
    catch (e) { setErr(`That did not go through: ${errorWords(e)}.`); } finally { setBusy(false); }
  };
  if (!open) return <ActionButton type="button" className="tk-chg" onClick={() => { setName(a.name); setOpen(true); }}>Change</ActionButton>;
  return (
    <span className="tk-ctl" role="group" aria-label={`Change the area ${a.name}`}>
      <ActionInput aria-label="Its name" value={name} onChange={(e) => setName(e.target.value)} />
      <ActionButton type="button" className="opt lead" disabled={busy || !name.trim() || name === a.name}
              onClick={() => void run(() => api.editArea(a.store.tenantId, a.id, { name: name.trim() }))}>Rename it</ActionButton>
      <ActionButton type="button" className="opt" disabled={busy || !empty}
              title={empty ? 'It holds nothing, so nothing moves' : 'Put its projects somewhere else first'}
              onClick={() => void run(async () => { await api.editArea(a.store.tenantId, a.id, { putAway: true }); say(`${a.name} put away.`); })}>Put it away</ActionButton>
      <ActionButton reading type="button" className="opt" disabled={busy} onClick={() => setOpen(false)}>Leave it</ActionButton>
      {err ? <span className="error">{err}</span> : null}
    </span>
  );
}

/**
 * And to a project: rename it, move it to another area, merge it into another project, or put it away. Putting
 * one away sends its tasks back to Not yet filed (U102, Mike: "put it away, keep what is in it"); a merge takes
 * them with it. Only a project somebody named can be renamed: a company's project is named by its company.
 */
function ProjectControls({ p, areas, projects, reload, say }: {
  p: ProjectView & { store: Store }; areas: Array<AreaView & { store: Store }>;
  projects: Map<string, ProjectView & { store: Store }>; reload: () => Promise<void>; say: (words: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(p.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<{ moved?: number } | unknown>, words?: (moved: number) => string) => {
    setBusy(true); setErr(null);
    try {
      const r = await fn() as { moved?: number };
      // Said through the page, not here: a project that has been put away unmounts this row with it, so a
      // message held in its own state is never seen. Driven, and that is exactly what happened.
      if (words) say(words(r?.moved ?? 0));
      setOpen(false);
      await reload();
    } catch (e) { setErr(`That did not go through: ${errorWords(e)}.`); } finally { setBusy(false); }
  };
  const others = [...projects.values()].filter((x) => x.id !== p.id && x.store.tenantId === p.store.tenantId);
  if (!open) {
    return (
      <p className="tk-ctl-row">
        <ActionButton type="button" className="tk-chg" onClick={() => { setName(p.name); setOpen(true); }}>Change this project</ActionButton>
      </p>
    );
  }
  return (
    <div className="tk-ctl" role="group" aria-label={`Change the project ${p.name}`}>
      {p.kind === 'named' ? (
        <>
          <ActionInput aria-label="Its name" value={name} onChange={(e) => setName(e.target.value)} />
          <ActionButton type="button" className="opt lead" disabled={busy || !name.trim() || name === p.name}
                  onClick={() => void run(() => api.editProject(p.store.tenantId, p.id, { name: name.trim() }))}>Rename it</ActionButton>
        </>
      ) : <span className="tk-ctl-note">Named by its {p.kind === 'company' ? 'company' : 'matter'}, so its name is not yours to change.</span>}
      <ActionSelect aria-label="Move it to another area" defaultValue="" disabled={busy}
              onChange={(e) => { if (e.target.value) void run(() => api.editProject(p.store.tenantId, p.id, { areaId: e.target.value })); }}>
        <option value="" disabled>Move it to…</option>
        {areas.filter((x) => x.id !== p.areaId && x.store.tenantId === p.store.tenantId).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </ActionSelect>
      {others.length === 0 ? null : (
        <ActionSelect aria-label="Merge it into another project" defaultValue="" disabled={busy}
                onChange={(e) => { if (e.target.value) { const into = others.find((x) => x.id === e.target.value); void run(() => api.editProject(p.store.tenantId, p.id, { mergeInto: e.target.value }), (moved) => `Merged into ${into?.name ?? 'it'}${moved ? `, with ${moved} ${moved === 1 ? 'task' : 'tasks'}` : ''}.`); } }}>
          <option value="" disabled>Merge it into…</option>
          {others.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </ActionSelect>
      )}
      <ActionButton type="button" className="opt" disabled={busy}
              onClick={() => void run(() => api.editProject(p.store.tenantId, p.id, { putAway: true }),
                (moved) => moved ? `Put away. Its ${moved} ${moved === 1 ? 'task is' : 'tasks are'} back in Not yet filed.` : 'Put away.')}>Put it away</ActionButton>
      <ActionButton reading type="button" className="opt" disabled={busy} onClick={() => setOpen(false)}>Leave it</ActionButton>
      {err ? <span className="error">{err}</span> : null}
    </div>
  );
}

function Directory({ rows, areas, projects, ...c }: Common & { rows: Row[]; areas: Array<AreaView & { store: Store; private: boolean }> }) {
  const [unfold, setUnfold] = useState<Set<string>>(new Set());
  const toggle = (k: string, set = setUnfold) => set((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  // An area the person opened or folded by hand; otherwise it is open where they have a task open (U377).
  const [byHand, setByHand] = useState<Map<string, boolean>>(new Map());
  // The task just added is shown where it sits: its area opens while it is marked (TK36).
  const justAdded = useContext(JustAdded);
  const live = rows.filter((r) => r.status !== 'done');
  const done = rows.filter((r) => r.status === 'done');
  const unfiled = live.filter((r) => unfiledIn(projects, r));
  const meOf = (st: Store) => c.views.get(st.tenantId)?.me ?? '';
  const yours = (r: Row) => isYours(r, meOf(r.store));
  const areaOf = (r: Row) => (r.projectId ? projects.get(r.projectId)?.areaId : undefined);
  // The areas where the reader has a task open lead, in their own order; then the rest (the drawing, U377).
  const hasYours = (a: AreaView) => live.some((r) => yours(r) && areaOf(r) === a.id);
  const ordered = [...areas.filter(hasYours), ...areas.filter((a) => !hasYours(a))];

  return (
    <div className="tk-dir">
      {ordered.map((a) => {
        const ps = [...projects.values()].filter((p) => p.areaId === a.id);
        const inArea = live.filter((r) => areaOf(r) === a.id);
        const mineHere = inArea.filter(yours);
        const overdue = mineHere.filter((r) => dueKind(r.dueAt, r.duePrecision) === 'overdue').length;
        const suggested = ps.reduce((n, p) => n + p.suggested, 0);
        const cards = cardOrder(ps, inArea);
        const open = inArea.some((r) => r.id === justAdded) || (byHand.get(a.id) ?? mineHere.length > 0);
        const setOpen = (v: boolean) => setByHand((m) => new Map(m).set(a.id, v));
        const lead = cards[0];
        return (
          <section key={a.id} className={`tk-area sa${a.private ? ' pv' : ''}${open ? '' : ' shut'}`} aria-label={a.name}>
            <div className="tk-areah">
              <h2><ActionButton reading type="button" className="link-h" aria-expanded={open} onClick={() => setOpen(!open)}><span className="caret" aria-hidden="true">{open ? '▾' : '▸'}</span>{a.name}</ActionButton></h2>
              <span className="f"><b className="big">{mineHere.length}</b> yours open</span>
              {overdue ? <span className="f w"><b className="big">{overdue}</b> overdue</span> : null}
              {suggested ? <span className="f"><b className="big">{suggested}</b> suggested</span> : null}
              {a.private ? <span className="c">private</span> : null}
              <span className="sp" />
              {open ? <AreaControls a={a} empty={ps.length === 0} reload={c.reload} say={c.say} /> : null}
              {ps.length ? <ActionButton reading type="button" className="opt small" onClick={() => setOpen(!open)}>{open ? 'Fold' : 'Open'}</ActionButton> : null}
            </div>
            {!open ? (
              <p className="tk-mostp">
                {ps.length === 0 ? <i>Nothing filed here yet.</i>
                  : lead && lead.tasks.length > 0 ? <><i>Most pressing:</i> <CardLogo p={lead.p} /><span><b>{lead.p.name}</b>: {lead.tasks[0]!.title}</span><DatePlate iso={lead.tasks[0]!.dueAt} precision={lead.tasks[0]!.duePrecision} /></>
                  : lead && lead.p.pressing ? <><i>Most pressing:</i> <CardLogo p={lead.p} /><span><b>{lead.p.name}</b>: {lead.p.pressing.title}</span><DatePlate iso={lead.p.pressing.dueAt} precision={lead.p.pressing.duePrecision} /></>
                  : lead ? <i>Nothing current: every suggestion here was due over a month ago.</i>
                  : <i>Nothing open and nothing suggested here.</i>}
              </p>
            ) : <AreaCards a={a} cards={cards} empty={ps.filter((p) => !cards.some((x) => x.p.id === p.id))} areas={areas} projects={projects} yours={yours} c={{ ...c, projects }} />}
          </section>
        );
      })}
      {unfiled.length > 0 ? (
        <section className="tk-area" aria-label="Not yet filed">
          <div className="tk-areah"><h2>Not yet filed</h2><span className="c">{unfiled.length} open</span><span className="sp" />
            <span className="c">{unfiled.filter((r) => r.filing).length === 0
              ? 'Cobrain has not read these yet.'
              : unfiled.some((r) => r.filing?.project || r.filing?.newProject)
                ? `Cobrain read ${unfiled.filter((r) => r.filing).length} of these and filed none. Where it had somewhere in mind, it is offered below.`
                : `Cobrain read ${unfiled.filter((r) => r.filing).length} of these and had nowhere to put them.`}</span></div>
          <div className="tk-tbl">{unfiled.map((r) => (
            <Fragment key={r.id}>
              <TaskBlock r={r} {...c} projects={projects} areas={areas} />
              <FilingOffer r={r} reload={c.reload} say={c.say} />
            </Fragment>
          ))}</div>
        </section>
      ) : null}
      {done.length > 0 ? (
        unfold.has('done') ? (
          <section className="tk-area" aria-label="Done this week">
            <div className="tk-areah"><h2><ActionButton reading type="button" className="link-h" aria-expanded onClick={() => toggle('done')}><span className="caret" aria-hidden="true">▾</span>Done this week</ActionButton></h2><span className="c">{done.length}</span></div>
            <div className="tk-tbl">{done.map((r) => <TaskBlock key={r.id} r={r} {...c} projects={projects} areas={areas} />)}</div>
          </section>
        ) : <ActionButton reading type="button" className="tk-areah folded" aria-expanded={false} onClick={() => toggle('done')}><h2><span className="caret" aria-hidden="true">▸</span>Done this week</h2><span className="c">{done.length}, ticked by you</span></ActionButton>
      ) : null}
    </div>
  );
}

/** A card on Structure (U377): a company or project, with the open tasks in it and what Cobrain suggests there. */
interface Card { p: ProjectView & { store: Store }; tasks: Row[]; rank: 0 | 1 | 2 | 3 }

/**
 * The cards of one area, in the order Mike asked for (U377): a project holding the reader's overdue or due-today work,
 * then one holding other open tasks, then one with a current suggestion by its date (the freshest slip first, then the
 * soonest), then one whose suggestions are all older, the most of them first. A project with none of these is not a
 * card: it is a name in the area's line of the rest (TK46).
 */
export function cardOrder<P extends ProjectView, R extends { projectId: string | null; dueAt: string; duePrecision: string }>(ps: readonly P[], open: readonly R[], now = Date.now()): Array<{ p: P; tasks: R[]; rank: 0 | 1 | 2 | 3 }> {
  const ms = (iso: string) => new Date(iso).getTime();
  const out = ps.flatMap((p) => {
    const tasks = open.filter((r) => r.projectId === p.id).sort((a, b) => a.dueAt.localeCompare(b.dueAt));
    const pressing = tasks.some((r) => { const k = dueKind(r.dueAt, r.duePrecision, now); return k === 'overdue' || k === 'today'; });
    const rank: 0 | 1 | 2 | 3 | null = tasks.length ? (pressing ? 0 : 1) : p.pressing ? 2 : p.older > 0 ? 3 : null;
    return rank === null ? [] : [{ p, tasks, rank }];
  });
  const slip = (iso: string) => { const t = ms(iso); return t < now ? -(now - t) * 0 + (now - t) : 1e15 + t; };   // past: newest first; then soonest
  return out.sort((a, b) => a.rank - b.rank
    || (a.rank <= 1 ? a.tasks[0]!.dueAt.localeCompare(b.tasks[0]!.dueAt) : 0)
    || (a.rank === 2 ? slip(a.p.pressing!.dueAt) - slip(b.p.pressing!.dueAt) : 0)
    || (a.rank === 3 ? b.p.older - a.p.older : 0)
    || (b.p.latest?.at ?? '').localeCompare(a.p.latest?.at ?? '') || a.p.name.localeCompare(b.p.name));
}

/** How many cards an area shows before the rest are a line of names: every card with work or a current suggestion, and
 *  cards of older suggestions only up to this many in all (the drawing shows six). */
const FIRST_CARDS = 6;

function AreaCards({ a, cards, empty, areas, projects, yours, c }: {
  a: AreaView & { store: Store }; cards: Card[]; empty: Array<ProjectView & { store: Store }>;
  areas: Array<AreaView & { store: Store }>; projects: Map<string, ProjectView & { store: Store }>; yours: (r: Row) => boolean; c: Common;
}) {
  const [all, setAll] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const lively = cards.filter((x) => x.rank < 3);
  const older = cards.filter((x) => x.rank === 3);
  const shownOlder = all ? older : older.slice(0, Math.max(0, FIRST_CARDS - lively.length));
  const hiddenOlder = older.filter((x) => !shownOlder.includes(x));
  const extra = [...hiddenOlder.map((x) => x.p), ...empty].filter((p) => picked.has(p.id));
  const shown: Card[] = [...lively, ...shownOlder, ...extra.map((p) => ({ p, tasks: [], rank: 3 as const }))];
  const names = [...hiddenOlder.map((x) => x.p), ...empty].filter((p) => !picked.has(p.id));
  const NAMES = 8;
  const nameList = all ? names : names.slice(0, NAMES);
  return (
    <>
      {shown.length ? (
        <ol className="tk-cards" aria-label={`${a.name}, by what is most pressing`}>
          {shown.map((x, i) => <ProjectCard key={x.p.id} n={i + 1} card={x} areas={areas} projects={projects} yours={yours} c={c} />)}
        </ol>
      ) : null}
      {names.length ? (
        <div className="tk-rest" role="group" aria-label={`${a.name} with nothing open`}>
          <span>{[hiddenOlder.filter((x) => !picked.has(x.p.id)).length ? `${hiddenOlder.filter((x) => !picked.has(x.p.id)).length} more with a suggestion` : null,
            empty.filter((p) => !picked.has(p.id)).length ? `${empty.filter((p) => !picked.has(p.id)).length} with nothing open` : null].filter(Boolean).join(', ')}:</span>
          {nameList.map((p) => <ActionButton reading key={p.id} type="button" className="nm" title={`Show ${p.name} as a card`} onClick={() => setPicked((s) => new Set(s).add(p.id))}>{p.name}</ActionButton>)}
          {names.length > nameList.length ? <span>and others</span> : null}
          {names.length > nameList.length || hiddenOlder.length ? <ActionButton reading type="button" className="opt small" onClick={() => setAll(!all)}>{all ? 'Show fewer' : 'Show all'}</ActionButton> : null}
        </div>
      ) : null}
    </>
  );
}

/** The company's logo from its record, or the project's initials (U377). */
function CardLogo({ p }: { p: ProjectView & { store: Store } }) {
  const line = useCompanyLine(p.store.tenantId, p.kind === 'company' ? p.organisationId : null);
  return <Logo name={p.name} logo={line?.logo ?? null} />;
}

function DatePlate({ iso, precision }: { iso: string; precision: string }) {
  const k = dueKind(iso, precision);
  const live = k === 'overdue' || k === 'today';
  return <span className={live ? 'plate' : 'plate quiet'}>{live ? <i aria-hidden="true" /> : null}{dueWords(iso, precision)}</span>;
}

function ProjectCard({ n, card, areas, projects, yours, c }: {
  n: number; card: Card; areas: Array<AreaView & { store: Store }>; projects: Map<string, ProjectView & { store: Store }>; yours: (r: Row) => boolean; c: Common;
}) {
  const { p, tasks } = card;
  const line = useCompanyLine(p.store.tenantId, p.kind === 'company' ? p.organisationId : null);
  const [showing, setShowing] = useState<null | 'current' | 'older'>(null);
  const [changing, setChanging] = useState(false);
  const mine = tasks.filter(yours).length;
  // What it is and who handles it, from the company record (U377): "Client, handled by Will Fyler, 3 open deals".
  const what = p.kind === 'company'
    ? [line?.kind ?? 'Company', line && !line.handledBy.none ? `handled by ${line.handledBy.bold}` : null,
       line && !line.deals.none ? [line.deals.bold, line.deals.text].filter(Boolean).join(' ') : null].filter(Boolean).join(', ')
    : p.kind === 'matter' ? 'Matter' : 'Project';
  const s = p.pressing;
  const plural = (k: number, one: string) => `${k} ${one}${k === 1 ? '' : 's'}`;
  return (
    <li className={`tk-pcard${tasks.length ? ' span' : ''}${card.rank === 3 ? ' old' : ''}${p.store.kind === 'personal' ? ' pv' : ''}`}>
      <span className="tk-cardno" aria-hidden="true">{n}</span>
      <div className="bd">
        <div className="tk-pcard-h">
          <Logo name={p.name} logo={line?.logo ?? null} />
          <div className="pc-n">
            {p.kind === 'company' && p.organisationId ? <CompanyName id={p.organisationId} name={p.name} className="tk-cardname" /> : <b className="tk-cardname">{p.name}</b>}
            <span>{what}</span>
          </div>
          <div className={mine ? 'pc-c' : 'pc-c zero'}><b>{mine}</b><span>{mine ? 'yours open' : 'none of yours'}</span></div>
        </div>
        {!tasks.length && s ? (
          <div className="tk-press">
            <span className="lbl">Most pressing from Cobrain, {p.current} current</span>
            <DatePlate iso={s.dueAt} precision={s.duePrecision} />
            <span className="tt">{s.title}{s.cue ? <i>{s.said === 'you' ? 'you said ' : s.said === 'them' && s.saidBy ? `${s.saidBy}: ` : ''}“{s.cue}”</i> : null}</span>
            <ActionButton type="button" className="opt lead" onClick={() => void c.onAct(suggestionRow(s, p.store), 'accept', {}, 'Accepted: it is in your list.')}>Accept</ActionButton>
          </div>
        ) : null}
        <p className="pc-last">{p.latest ? <><i>Last heard: </i>{p.latest.text}<i>, {dueWords(p.latest.at, 'day')}</i></> : <i>Nothing heard on record yet</i>}</p>
        {tasks.length && p.current ? <p className="oldn">{plural(p.current, 'current suggestion')} from Cobrain. <ActionButton reading type="button" className="link inline" aria-expanded={showing === 'current'} onClick={() => setShowing(showing === 'current' ? null : 'current')}>{showing === 'current' ? 'Hide' : 'Show'}</ActionButton></p> : null}
        {p.older ? <p className="oldn">{plural(p.older, 'older suggestion')}, due over a month ago. <ActionButton reading type="button" className="link inline" aria-expanded={showing === 'older'} onClick={() => setShowing(showing === 'older' ? null : 'older')}>{showing === 'older' ? 'Hide' : 'Show'}</ActionButton></p> : null}
        {showing ? <div className="tk-tbl"><ProjectSuggestions store={p.store} projectId={p.id} projects={projects} areas={areas} team={c.views.get(p.store.tenantId)?.team ?? []} onAct={c.onAct} only={showing} of={showing === 'current' ? p.current : p.older} /></div> : null}
        {tasks.length ? <div className="tk-tbl pc-tasks">{tasks.map((r) => <TaskBlock key={r.id} r={r} {...c} projects={projects} areas={areas} />)}</div> : null}
        {changing ? <ProjectControls p={p} areas={areas} projects={projects} reload={c.reload} say={c.say} /> : null}
        <p className="tk-ctl-row"><ActionButton type="button" className="tk-chg" aria-expanded={changing} onClick={() => setChanging(!changing)}>{changing ? 'Leave it' : 'Change'}</ActionButton></p>
      </div>
    </li>
  );
}

/**
 * What Cobrain decided about a task it read and filed nowhere, and the one click that takes it (U61; B803).
 *
 * Every one of the 126 unfiled tasks on the live store has been read, and every one was judged below the bar in the
 * filing: the answer went to audit.judgement and was thrown away, and the row said "Not yet filed" as though nobody
 * had looked. 47 of them name somewhere. This says when Cobrain looked, and offers the name where there is one.
 *
 * It never says WHY nothing was filed. A filing withheld because its subject is one Cobrain never writes about
 * (module 05 R44) reads exactly like one that fitted nowhere: on a row about one task, naming the subject IS the
 * disclosure. A withheld filing carries no project, so it offers nothing, and needs no case of its own here.
 *
 * It says what happened through the page's strip where there is one, and in its own line where there is not. Its own
 * line alone is not enough: the moment the filing lands, the row is filed and the offer unmounts with it, so the
 * person sees nothing -- the same fault the project controls had (U102), fixed the same way.
 */
function FilingOffer({ r, reload, say }: { r: Row; reload: Common['reload']; say?: Common['say'] }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const f = r.filing;
  if (!f || (!f.project && !f.newProject && !said)) {
    // Read, and nowhere to put it: worth saying once, so the row is not mistaken for one nobody has looked at.
    return f && !said ? <p className="tk-filing"><span className="tk-fread">Cobrain read this {agoWords(f.readAt)} and had nowhere to put it.</span></p> : null;
  }
  if (said) return <p className="tk-filing"><span className="tk-fread">{said}</span></p>;
  const take = async (body: { projectId?: string; areaId?: string; name?: string }, words: string) => {
    setBusy(true); setErr(null);
    try { await api.acceptFiling(r.store.tenantId, r.id, body); setSaid(words); say?.(words); await reload(); }
    catch (e) { setErr(errorWords(e)); setBusy(false); }
  };
  return (
    <p className="tk-filing" data-task={r.id}>
      <span className="tk-fread">Cobrain read this {agoWords(f.readAt)} and filed nothing.</span>
      {f.project ? (
        <ActionButton type="button" className="link" disabled={busy}
                onClick={() => void take({ projectId: f.project!.id }, `Filed in ${f.project!.name}.`)}>
          It had <b>{f.project.name}</b> in mind — file it there
        </ActionButton>
      ) : (
        <ActionButton type="button" className="link" disabled={busy}
                onClick={() => void take({ areaId: f.newProject!.areaId, name: f.newProject!.name }, `Made ${f.newProject!.name} and filed it there.`)}>
          It would have made <b>{f.newProject!.name}</b> in {f.newProject!.areaName} — make it and file it there
        </ActionButton>
      )}
      {err ? <span className="error">{err}</span> : null}
    </p>
  );
}

/** "N of M" when a list draws fewer than there are (BUGS.md TK31, TK32), else null: nothing is cut in silence. */
function cutOf(shown: number, total: number): string | null {
  return total > shown ? `${shown.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')}` : null;
}

/** The suggestions filed under one project, opened with its row (D60: none is the person's until accepted). */
function ProjectSuggestions({ store, projectId, projects, areas, team, onAct, only, of }: {
  store: Store; projectId: string; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>;
  team: TeamPerson[]; onAct: Common['onAct'];
  /** A card's Show (U377): its current suggestions only, or its older ones only, newest first. */
  only?: 'current' | 'older';
  /** How many the project's card counts for `only` (BUGS.md TK31): the read stops at 200, and the page says so. */
  of?: number;
}) {
  const [items, setItems] = useState<SuggestionView[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.projectSuggestions(store.tenantId, projectId).then((r) => setItems(r.suggestions)).catch(() => { setErr(true); setItems([]); }); }, [store.tenantId, projectId]);
  const [older, setOlder] = useState(false);
  if (!items) return <p className="tk-none">Reading what Cobrain suggests here…</p>;
  if (err) return <p className="tk-none">Cobrain's suggestions here could not be read. Open the project again to retry.</p>;
  // Current first; anything more than a month overdue is folded behind a count, never removed.
  const cut = Date.now() - 30 * 86_400_000;
  const current = items.filter((s) => new Date(s.dueAt).getTime() >= cut);
  const old = items.filter((s) => new Date(s.dueAt).getTime() < cut);
  if (only) {
    const list = only === 'current' ? current : [...old].sort((a, b) => b.dueAt.localeCompare(a.dueAt));
    const cut = of !== undefined ? cutOf(list.length, of) : null;
    return list.length === 0 && !cut ? <p className="tk-none">Nothing here now.</p> : <>{list.map((s) => (
      <SuggestionLine key={s.id} s={s} store={store} projects={projects} areas={areas} team={team} onAct={onAct} inDir
                      onGone={() => setItems((x) => x?.filter((y) => y.id !== s.id) ?? null)} />
    ))}{cut ? <p className="tk-none tk-cut">{cut} shown.</p> : null}</>;
  }
  return (
    <>
      {(older ? [...current, ...old] : current).map((s) => (
        <SuggestionLine key={s.id} s={s} store={store} projects={projects} areas={areas} team={team} onAct={onAct} inDir
                        onGone={() => setItems((x) => x?.filter((y) => y.id !== s.id) ?? null)} />
      ))}
      {current.length === 0 && !older && old.length > 0 ? <p className="tk-none">Nothing current: every suggestion here was due more than a month ago.</p> : null}
      {old.length > 0 && !older ? <ActionButton reading type="button" className="tk-more in-dir" onClick={() => setOlder(true)}>{old.length} older, more than a month overdue. Show</ActionButton> : null}
    </>
  );
}

/**
 * The Auto task manager's bar (module 04 D240, module 10 U80): its switch, on by default, and the detail setting,
 * five steps with the current one written out in words. The setting gates what Cobrain may PROPOSE and never what
 * it reads: every conversation is read at every step.
 */
/**
 * What each step admits, in the person's words (B954). These replace the old five, which named unrelated tests --
 * "belongs to a deal", then "carries a date or money", then "has a date at all" -- and on Mike's own store
 * admitted 75, then 704, then 724, then everything, twice. Two rungs were the same test and one could take away
 * what the rung above gave. The steps now read one scale, how firm the promise was, so each one can only add.
 */
const DETAIL_WORDS = [
  'Only what you plainly promised',
  'And what you clearly undertook to do',
  'And what you said you would look at',
  'And what you suggested doing',
  'Everything, down to a passing intention',
];

function AutoBar({ store, settings, reload }: { store: Store; settings: { autoTasksOn: boolean; detail: number }; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // Moving it now moves the list, so the bar says what it did. Mike, on the old one: "the changes in result are
  // very poor moving from high level to low level and the tasks do not increase." It was inert; this is the proof
  // that it is not, in the only place he would look for it.
  const save = async (body: { autoTasksOn?: boolean; detail?: number }) => {
    setBusy(true); setErr(null); setSaid(null);
    try {
      const r = await api.setSettings(store.tenantId, body);
      const back = r.restored ?? 0, gone = r.retired ?? 0;
      setSaid(back === 0 && gone === 0 ? 'Nothing moved: every task you have is inside that step.'
        : [back ? `${back} ${back === 1 ? 'task is' : 'tasks are'} back` : '', gone ? `${gone} put away` : '']
            .filter(Boolean).join(', ') + '.');
      await reload();
    }
    catch (e) { setErr(`That did not save: ${errorWords(e)}.`); }
    finally { setBusy(false); }
  };
  const step = Math.min(5, Math.max(1, settings.detail));
  return (
    <div className="tk-auto">
      <div className="tk-auto-l">
        <b>Auto task manager</b>
        <ActionButton type="button" role="switch" aria-checked={settings.autoTasksOn} aria-label="Auto task manager" disabled={busy}
                className={settings.autoTasksOn ? 'tk-switch on' : 'tk-switch'} onClick={() => void save({ autoTasksOn: !settings.autoTasksOn })}>
          <i aria-hidden="true" />
        </ActionButton>
        <span className="tk-auto-note">{settings.autoTasksOn ? 'raising tasks from what is said across mail, chat and meetings' : 'off: it reads everything, and proposes nothing'}</span>
      </div>
      <div className="tk-auto-r">
        <span id="tk-detail-l">Detail</span>
        <span className="tk-steps" role="radiogroup" aria-labelledby="tk-detail-l">
          {[1, 2, 3, 4, 5].map((n) => (
            <ActionButton key={n} type="button" role="radio" aria-checked={n === step} aria-label={DETAIL_WORDS[n - 1]} disabled={busy || !settings.autoTasksOn}
                    className={n === step ? 'tk-step on' : 'tk-step'} onClick={() => void save({ detail: n })} />
          ))}
        </span>
        <b className="tk-detail-words">{DETAIL_WORDS[step - 1]}</b>
        {said ? <span className="tk-detail-said" role="status">{said}</span> : null}
      </div>
      {err ? <p className="error" role="alert">{err}</p> : null}
      <Doing store={store} />
    </div>
  );
}

/**
 * What the Auto task manager has been doing (module 10 U80, the drawing's line and link; BUGS.md TK13): "Added 6, closed 9,
 * raised 2 since you last looked", opening that short list, and "What it has been doing", opening the last thirty days,
 * each change with its reason. When the person last looked is kept in this browser, per store (a build choice: no
 * column holds it); the first look counts the last seven days and says so.
 */
const lookedKey = (tenantId: string) => `cobrain.tasks.lookedAt.${tenantId}`;
const readLooked = (tenantId: string): string | null => { try { return localStorage.getItem(lookedKey(tenantId)); } catch { return null; } };
const writeLooked = (tenantId: string, at: string) => { try { localStorage.setItem(lookedKey(tenantId), at); } catch { /* counted again next time */ } };
const FIRST_LOOK_DAYS = 7;

export function doingLine(d: Pick<TaskDoing, 'added' | 'closed' | 'raised'>, looked: boolean): string | null {
  const parts = [d.added ? `Added ${d.added.toLocaleString('en-GB')}` : null, d.closed ? `closed ${d.closed.toLocaleString('en-GB')}` : null, d.raised ? `raised ${d.raised.toLocaleString('en-GB')}` : null]
    .filter((x): x is string => x !== null);
  if (parts.length === 0) return null;
  const head = parts.join(', ');
  return `${head[0]!.toUpperCase()}${head.slice(1)} ${looked ? 'since you last looked' : `in the last ${FIRST_LOOK_DAYS} days`}.`;
}

const DOING_WORDS: Record<TaskDoingItem['kind'], string> = { added: 'Added', closed: 'Closed', raised: 'Raised', lowered: 'Lowered' };

function Doing({ store }: { store: Store }) {
  const [since] = useState(() => readLooked(store.tenantId));
  const [recent, setRecent] = useState<TaskDoing | null>(null);
  const [all, setAll] = useState<TaskDoing | null>(null);
  const [open, setOpen] = useState<'recent' | 'all' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let gone = false;
    const from = since ?? new Date(Date.now() - FIRST_LOOK_DAYS * 86_400_000).toISOString();
    api.doing(store.tenantId, from).then((d) => { if (!gone) { setRecent(d); writeLooked(store.tenantId, new Date().toISOString()); } }).catch(() => undefined);
    return () => { gone = true; };
  }, [store.tenantId, since]);
  const showAll = async () => {
    if (open === 'all') { setOpen(null); return; }
    setErr(null);
    try { setAll(all ?? await api.doing(store.tenantId, null)); setOpen('all'); }
    catch (e) { setErr(`That did not load: ${errorWords(e)}.`); }
  };
  const line = recent ? doingLine(recent, since !== null) : null;
  const list = open === 'recent' ? recent : open === 'all' ? all : null;
  return (
    <div className="tk-doing">
      <p className="tk-doing-l">
        {line ? <span>{line}</span> : null}
        {line ? <ActionButton reading type="button" className="hx-link" aria-expanded={open === 'recent'} onClick={() => setOpen(open === 'recent' ? null : 'recent')}>{open === 'recent' ? 'Hide them' : 'See them'}</ActionButton> : null}
        <span className="sp" />
        <ActionButton reading type="button" className="hx-link" aria-expanded={open === 'all'} onClick={() => void showAll()}>What it has been doing</ActionButton>
      </p>
      {err ? <p className="error" role="alert">{err}</p> : null}
      {list ? (
        list.items.length === 0
          ? <p className="tk-doing-none">Nothing in the last thirty days: it has added, closed and moved nothing of yours.</p>
          : <ul className="tk-doing-list" aria-label={open === 'all' ? 'What it has been doing' : 'What changed since you last looked'}>
              {list.items.map((x) => (
                <li key={`${x.kind}-${x.taskId}-${x.at}`}>
                  <span className="k">{DOING_WORDS[x.kind]}{x.move ? ` ${x.move}` : ''}</span>
                  <span className="t">{x.title}</span>
                  {x.why ? <span className="w">{x.kind === 'raised' || x.kind === 'lowered' ? x.why : `“${x.why}”`}</span> : null}
                  <span className="d">{dayMonth(new Date(x.at))}</span>
                </li>
              ))}
            </ul>
      ) : null}
      {list && list.items.length > 0 && list.items.length < list.added + list.closed + list.raised + list.lowered
        ? <p className="tk-doing-none">The newest {list.items.length} of {(list.added + list.closed + list.raised + list.lowered).toLocaleString('en-GB')}.</p>
        : null}
    </div>
  );
}

/**
 * The master list (U80): everything open, from every area and both stores, in one order. A proposal sits in its
 * own place in that order, marked, with Accept and Not a task on the row (D239); the date and the priority are
 * edited on the row itself, and once a person sets either, the Auto task manager leaves it alone (migration 136).
 *
 * The order is `masterOrder` and nothing else decides it (U92). Mike read the old one on his own store on
 * 2026-09-20 and called it "utter dog shit": it put everything overdue in one bucket and drew the oldest date
 * first, so his page opened on three promises from September 2025. What is more than a month past its date now
 * leaves the list for the folded group at the foot.
 */
function MasterList({ order, proposed, done, proposalTotal, filtering, projects, areas, team, ...c }: Common & {
  /** The reader's own tasks, in the master order, and the long overdue among them apart. */
  order: { list: Entry[]; long: Entry[] };
  /** Cobrain's proposals the page holds, most pressing first, drawn in their own fold beneath (U374). */
  proposed: Entry[];
  done: Row[];
  proposalTotal: number;
  /** A filter is set, so the fold counts the proposals it leaves, not all of them. */
  filtering: boolean;
  areas: Array<AreaView & { store: Store; private: boolean }>;
  team: TeamPerson[];
}) {
  const [showDone, setShowDone] = useState(false);
  const [showLong, setShowLong] = useState(false);
  const [showProposed, setShowProposed] = useState(false);
  const proposedCount = (filtering ? proposed.length : proposalTotal).toLocaleString('en-GB');
  const draw = (x: Entry) => x.p
    ? (
      <Fragment key={x.r.id}>
        <SuggestionLine s={x.p} store={x.r.store} projects={projects} areas={areas} team={team} onAct={c.onAct}
                        onGone={() => void c.reload()} inMaster
                        open={c.open === x.r.id} onToggle={() => c.setOpen(c.open === x.r.id ? null : x.r.id)} />
        {c.open === x.r.id
          ? <Opened r={x.r} team={team} projects={projects} areas={areas} onAct={c.onAct} reload={c.reload} go={c.go} say={c.say} proposed />
          : null}
      </Fragment>
    )
    : <MasterRow key={x.r.id} r={x.r} projects={projects} areas={areas} {...c} />;

  return (
    <div className="tk-dir">
      <div className="tk-tbl">
        {order.list.length === 0
          ? <p className="tk-none">{order.long.length === 0 ? 'Nothing open. Cobrain is reading; what you owe will appear here.' : 'Nothing pressing. Everything you have open is more than a month past its date, below.'}</p>
          : order.list.map(draw)}
      </div>
      {proposed.length > 0 ? (
        <section className="tk-fold" aria-label="Suggested by Cobrain">
          <div className="tk-foldh">
            <div>
              <h2><span className="n">{proposedCount}</span> suggested by Cobrain</h2>
              <p>Read from what you said in mail, chat and meetings, the most pressing first. Accept one and it joins your list.</p>
            </div>
            <ActionButton reading type="button" className="opt" aria-expanded={showProposed} onClick={() => setShowProposed(!showProposed)}>
              {showProposed ? 'Hide them again' : `Show the ${proposedCount}`}
            </ActionButton>
          </div>
          {showProposed ? <div className="tk-tbl">{proposed.map(draw)}</div> : null}

        </section>
      ) : null}
      {order.long.length > 0 ? (
        showLong ? (
          <section className="tk-area" aria-label="Long overdue">
            <div className="tk-areah"><h2><ActionButton reading type="button" className="link-h" aria-expanded onClick={() => setShowLong(false)}><span className="caret" aria-hidden="true">▾</span>Long overdue, probably done</ActionButton></h2><span className="c">{order.long.length}</span></div>
            <p className="tk-note">More than a month past its date. Most of this was done long ago; nothing here is closed until what was said afterwards proves it.</p>
            <div className="tk-tbl">{order.long.map(draw)}</div>
          </section>
        ) : (
          <ActionButton reading type="button" className="tk-areah folded" aria-expanded={false} onClick={() => setShowLong(true)}>
            <h2><span className="caret" aria-hidden="true">▸</span>Long overdue, probably done</h2><span className="c">{order.long.length}</span>
          </ActionButton>
        )
      ) : null}
      {done.length > 0 ? (
        showDone ? (
          <section className="tk-area" aria-label="Done this week">
            <div className="tk-areah"><h2><ActionButton reading type="button" className="link-h" aria-expanded onClick={() => setShowDone(false)}><span className="caret" aria-hidden="true">▾</span>Done this week</ActionButton></h2><span className="c">{done.length}</span></div>
            <div className="tk-tbl">{done.map((r) => <MasterRow key={r.id} r={r} projects={projects} areas={areas} {...c} />)}</div>
          </section>
        ) : (
          <ActionButton reading type="button" className="tk-areah folded" aria-expanded={false} onClick={() => setShowDone(true)}>
            <h2><span className="caret" aria-hidden="true">▸</span>Done this week</h2><span className="c">{done.length}</span>
          </ActionButton>
        )
      ) : null}
    </div>
  );
}

/** A row of the master list: the task as it is drawn everywhere, with its date and priority edited in place (U80). */
function MasterRow({ r, projects, areas, open, setOpen, onAct, views, reload, go, say }: Common & { r: Row; areas: Array<AreaView & { store: Store }> }) {
  const [err, setErr] = useState<string | null>(null);
  const edit = async (body: Record<string, unknown>) => {
    setErr(null);
    try { await api.edit(r.store.tenantId, r.id, body); await reload(); }
    catch (e) { setErr(`That did not go through: ${errorWords(e)}.`); }
  };
  return (
    <>
      <TaskBlock r={r} projects={projects} areas={areas} open={open} setOpen={setOpen} onAct={onAct} views={views} reload={reload} go={go} say={say} master edit={edit} />
      {err ? <p className="tk-rowerr error" role="alert">{err}</p> : null}
    </>
  );
}

function ByWhen({ rows, projects, areas, ...c }: Common & { rows: Row[]; areas: Array<AreaView & { store: Store; private: boolean }> }) {
  const [unfold, setUnfold] = useState<Set<string>>(new Set(['overdue', 'today']));
  const sections: Array<[string, string, Row[]]> = [
    ['overdue', 'Overdue', rows.filter((r) => r.status !== 'done' && dueKind(r.dueAt, r.duePrecision) === 'overdue')],
    ['today', 'Today', rows.filter((r) => r.status !== 'done' && dueKind(r.dueAt, r.duePrecision) === 'today')],
    ['week', 'Next 7 days', rows.filter((r) => r.status !== 'done' && dueKind(r.dueAt, r.duePrecision) === 'week')],
    ['later', 'Later', rows.filter((r) => r.status !== 'done' && dueKind(r.dueAt, r.duePrecision) === 'later')],
    ['done', 'Done this week', rows.filter((r) => r.status === 'done')],
  ];
  return (
    <div className="tk-dir">
      {sections.map(([k, label, list]) => list.length === 0 ? null : unfold.has(k) ? (
        <section key={k} className="tk-area" aria-label={label}>
          <div className="tk-areah"><h2><ActionButton reading type="button" className="link-h" aria-expanded onClick={() => setUnfold((s) => { const n = new Set(s); n.delete(k); return n; })}><span className="caret" aria-hidden="true">▾</span>{label}</ActionButton></h2><span className="c">{list.length}</span></div>
          <div className="tk-tbl wide">{list.map((r) => <TaskBlock key={r.id} r={r} {...c} projects={projects} areas={areas} showWhere />)}</div>
        </section>
      ) : (
        <ActionButton reading key={k} type="button" className="tk-areah folded" aria-expanded={false} onClick={() => setUnfold((s) => new Set(s).add(k))}><h2><span className="caret" aria-hidden="true">▸</span>{label}</h2><span className="c">{list.length}</span></ActionButton>
      ))}
    </div>
  );
}

function TaskBlock({ r, projects, areas, open, setOpen, onAct, views, reload, go, say, showWhere, master, edit, asPage }: Common & {
  r: Row; areas: Array<AreaView & { store: Store }>; showWhere?: boolean;
  /** The master list draws one line only: where it is filed goes under the title, and the date and priority are edited on the row. */
  master?: boolean; edit?: (body: Record<string, unknown>) => Promise<void>;
  /** The task opened as a page (U48): opened always, and its Back is the toggle. */
  asPage?: boolean;
}) {
  const [assigning, setAssigning] = useState(false);
  const isOpen = open === r.id;
  return (
    <>
      <TaskLine r={r} projects={projects} areas={areas} open={isOpen} onToggle={() => setOpen(isOpen ? null : r.id)} onAct={onAct}
                showWhere={showWhere ?? false} master={master ?? false} edit={edit} assigning={assigning} onAssign={() => setAssigning(!assigning)} />
      {assigning ? <AssignPick r={r} onCancel={() => setAssigning(false)} onPick={(p) => { setAssigning(false); void onAct(r, 'assign', { to: p.principalId }, assignedWords(p, false), 'withdraw'); }} /> : null}
      {isOpen ? <Opened r={r} team={views.get(r.store.tenantId)?.team ?? []} projects={projects} areas={areas} onAct={onAct} reload={reload} go={go} say={say} asPage={asPage ?? false} /> : null}
    </>
  );
}

function whereOf(r: Row, projects: Map<string, ProjectView & { store: Store }>, areas: Array<AreaView & { store: Store }>): { area: string; project: string; organisationId: string | null } | null {
  const p = r.projectId ? projects.get(r.projectId) : undefined;
  const a = p ? areas.find((x) => x.id === p.areaId) : undefined;
  return p && a ? { area: a.name, project: p.name, organisationId: p.kind === 'company' ? p.organisationId : null } : null;
}

/** A project's name; a company's project names the company, so it opens the record (BUGS.md X30, U356). Not inside a button. */
/** An opened company project leads with the company's line (U353), its name a link to the record (X30). */
function ProjectLine({ store, id, name }: { store: string; id: string; name: string }) {
  const line = useCompanyLine(store, id);
  return <div className="tk-line"><LineOrName id={id} name={name} line={line} /></div>;
}

function ProjectName({ where }: { where: { project: string; organisationId: string | null } }) {
  return where.organisationId ? <CompanyName id={where.organisationId} name={where.project} /> : <>{where.project}</>;
}

function TaskLine({ r, projects, areas, open, onToggle, onAct, showWhere, master, edit, assigning, onAssign }: {
  r: Row; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>; open: boolean; onToggle: () => void; showWhere: boolean;
  master?: boolean; edit?: ((body: Record<string, unknown>) => Promise<void>) | undefined;
  /** Assign's picker is open under this row (U298). */
  assigning: boolean; onAssign: () => void;
  onAct: (r: Row, a: TaskAct, extra?: Parameters<typeof api.act>[3], words?: string, undo?: TaskAct) => Promise<void>;
}) {
  const kind = dueKind(r.dueAt, r.duePrecision);
  const done = r.status === 'done';
  const where = whereOf(r, projects, areas);
  const writable = r.mine || r.store.kind === 'personal';
  // On the master list the filing goes under the title (the drawing's one-line row), not in a column of its own.
  const filed = where ? <><b>{where.area}</b> › {where.project}</> : <span className="unfiled">Not yet filed</span>;
  const canEdit = Boolean(master && edit && writable && !done);
  const fresh = useContext(JustAdded) === r.id;
  return (
    <div className={`tk-row${open ? ' open' : ''}${r.private ? ' pv' : ''}${done ? ' done' : ''}${showWhere ? ' wide' : ''}${master ? ' master' : ''}${fresh ? ' fresh' : ''}`} data-task={r.id}>
      <ActionButton type="button" className={done ? 'tick done' : 'tick'} aria-label={done ? `Not done: ${r.title}` : `Mark done: ${r.title}`} disabled={!writable}
              title={writable ? undefined : 'Only its owner ticks it'}
              onClick={() => void (done ? onAct(r, 'undone') : onAct(r, 'done', {}, `Done: ${r.title}.`, 'undone'))} />
      <ActionButton reading type="button" className="t" aria-expanded={open} onClick={onToggle}>
        <span className="tt">{r.title}</span>
        <span className="sub">
          {fresh ? <span className="newtag">Just added</span> : null}
          {master ? <span className="filed">{filed}</span> : null}
          {r.steps.total > 0 ? <span>{r.steps.done} of {r.steps.total} steps</span> : null}
          {r.booked ? <span>{minutesWords(Math.round((new Date(r.booked.end).getTime() - new Date(r.booked.start).getTime()) / 60000))} booked {dueWords(r.booked.start, 'minute')}</span> : null}
          {r.chatMessages > 0 ? <span>chat, {r.chatMessages}</span> : null}
          {r.status === 'in_progress' ? <span className="tk-state">In progress</span> : null}
          {r.private ? <span className="pvtag">Private, only you</span> : null}
          {/* On a phone the person column is gone: the hand-on is said under the title instead. */}
          {r.delegateId && r.mine ? <span className="sub-del">→ {r.delegateName ?? 'someone'}{quietFor(r) !== null ? <b>, quiet {r.quietDays} days</b> : null}</span> : null}
          {r.askedBy ? <span className="tk-asked">{r.askedBy.name} asks you to take it</span> : null}
        </span>
      </ActionButton>
      {showWhere ? <span className="where">{where ? <><b>{where.area}</b> › <ProjectName where={where} /></> : filed}</span> : null}
      {canEdit && edit
        ? <DuePlate r={r} onSet={(dueAt) => edit({ dueAt })} />
        : <span className={!done && (kind === 'overdue' || kind === 'today') ? 'plate' : 'plate quiet'}>{!done && (kind === 'overdue' || kind === 'today') ? <i aria-hidden="true" /> : null}{done && r.doneAt ? `done ${dueWords(r.doneAt, 'day')}` : dueWords(r.dueAt, r.duePrecision)}</span>}
      <RowChoices r={r} onAct={onAct} assigning={assigning} onAssign={onAssign} />
      {canEdit && edit
        ? <ActionSelect className={r.priority === 1 ? 'pri p1' : 'pri'} aria-label={`Priority ${r.priority} of 4, ${r.title}${r.prioritySource === 'person' ? ', yours' : ", Cobrain's until you change it"}`}
                  title={`${priorityWords(r.priority)}${r.prioritySource === 'person' ? ', yours' : ", Cobrain's until you change it"}`}
                  value={r.priority} onChange={(e) => void edit({ priority: Number(e.target.value) })}>
            {[1, 2, 3, 4].map((x) => <option key={x} value={x}>{x}</option>)}
          </ActionSelect>
        : <span className={r.priority === 1 ? 'pri p1' : 'pri'} aria-label={`Priority ${r.priority}`} title={priorityWords(r.priority)}>P{r.priority}</span>}
    </div>
  );
}

/**
 * The date plate, which is also how the date is changed (U80: "editable on the row"). It stays a plate, with the
 * words the page says everywhere ("Today 17:00", "Thu 18"), until it is pressed; then it is a date field in the
 * same place. It was a second line of controls under every row until 2026-09-20, which doubled the height of the
 * list and is half of what made it unreadable.
 */
function DuePlate({ r, onSet }: { r: Row; onSet: (dueAt: string) => void | Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const kind = dueKind(r.dueAt, r.duePrecision);
  const live = kind === 'overdue' || kind === 'today';
  const mine = r.dueSource === 'person';
  if (editing) {
    return (
      <ActionInput type="date" className="plate-edit" autoFocus aria-label={`Due date, ${r.title}`} defaultValue={inputDate(r.dueAt)}
             onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
             onBlur={() => setEditing(false)}
             onChange={(e) => { const v = fromInputDate(e.target.value); if (v) { setEditing(false); void onSet(v); } }} />
    );
  }
  return (
    <ActionButton type="button" className={live ? 'plate set' : 'plate quiet set'}
            aria-label={`Due ${dueWords(r.dueAt, r.duePrecision)}${mine ? ', yours' : ", Cobrain's until you change it"}. Change the date`}
            title={mine ? 'You set this date' : "Cobrain set this date, until you change it"}
            onClick={() => setEditing(true)}>
      {live ? <i aria-hidden="true" /> : null}{dueWords(r.dueAt, r.duePrecision)}
    </ActionButton>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// A task opened in place (U48): the actions, the fields, the steps, where it came from, the conversation.
// ---------------------------------------------------------------------------------------------------------------
function Opened({ r, team, projects, areas, onAct, reload, asPage, go, proposed, say }: {
  r: Row; team: TeamPerson[]; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>;
  onAct: (r: Row, a: TaskAct, extra?: Parameters<typeof api.act>[3], words?: string, undo?: TaskAct) => Promise<void>; reload: () => Promise<void>; asPage?: boolean; go: (path: string) => void;
  /** A proposal Cobrain raised, opened to be read before it is answered (U93). Its answers are not a task's. */
  proposed?: boolean;
  /** The page's strip, for what outlives the row it was said on. */
  say?: Common['say'];
}) {
  const [d, setD] = useState<TaskDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<null | 'hand' | 'park' | 'move' | 'assign'>(null);
  const [step, setStep] = useState('');
  const [notes, setNotes] = useState(r.notes ?? '');
  const [booking, setBooking] = useState(false);
  const store = r.store.tenantId;
  const writable = r.mine || r.store.kind === 'personal';
  const where = whereOf(r, projects, areas);
  const H = asPage ? 'h2' : 'h3';   // under the page's h1, or under an area's h2 in the list

  const refresh = useCallback(async () => {
    try { setD(await api.detail(store, r.id)); } catch (e) { setErr(`This task could not be opened: ${errorWords(e)}.`); }
  }, [store, r.id]);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { setNotes(r.notes ?? ''); }, [r.notes]);

  const edit = async (body: Record<string, unknown>) => {
    try { await api.edit(store, r.id, body); await reload(); } catch (e) { setErr(`That did not go through: ${errorWords(e)}.`); }
  };

  return (
    <div className={asPage ? 'tk-open page' : 'tk-open'}>
      {err ? <p className="error" role="alert">{err}</p> : null}
      {r.projectId ? null : (say ? <FilingOffer r={r} reload={reload} say={say} /> : <FilingOffer r={r} reload={reload} />)}
      {writable ? (
        <div className="tk-bar">
          {proposed ? (
            <>
              <ActionButton type="button" className="opt lead" onClick={() => void onAct(r, 'accept', {}, 'Accepted: it is in your list.')}>Accept</ActionButton>
              {r.store.kind === 'personal' ? null
                : <ActionButton type="button" className={mode === 'assign' ? 'opt on' : 'opt'} aria-expanded={mode === 'assign'} onClick={() => setMode(mode === 'assign' ? null : 'assign')}>Assign</ActionButton>}
              <ActionButton type="button" className="opt" onClick={() => void onAct(r, 'not_a_task', {}, `Removed: ${r.title}. Cobrain will learn from it.`, 'restore')}>Remove</ActionButton>
              {asPage ? null : <ActionButton reading type="button" className="opt" onClick={() => go(`/tasks/${r.id}`)}>Open as a page</ActionButton>}
            </>
          ) : <>
          {r.status === 'done'
            ? <ActionButton type="button" className="opt lead" onClick={() => void onAct(r, 'undone')}>Not done after all</ActionButton>
            : <ActionButton type="button" className="opt lead" onClick={() => void onAct(r, 'done', {}, `Done: ${r.title}.`, 'undone')}>Mark done</ActionButton>}
          {r.booked
            ? <ActionButton type="button" className="opt" onClick={async () => { try { await api.unbook(store, r.id); await reload(); } catch (e) { setErr(`That did not go through: ${errorWords(e)}.`); } }}>Unbook {dueWords(r.booked.start, 'minute')}</ActionButton>
            : <ActionButton type="button" className="opt" disabled={booking} onClick={async () => {
                setBooking(true); setErr(null);
                try { await api.book(store, r.id, r.minutesNeeded ?? undefined); await reload(); } catch (e) { setErr(`${errorWords(e)}.`); } finally { setBooking(false); }
              }}>{booking ? 'Finding a slot…' : 'Book time'}</ActionButton>}
          {/* Handing on and asking someone to take it are one or the other: withdraw the ask first (U298). */}
          {r.delegateId || r.asked.length > 0 ? null : <ActionButton type="button" className={mode === 'hand' ? 'opt on' : 'opt'} aria-expanded={mode === 'hand'} onClick={() => setMode(mode === 'hand' ? null : 'hand')}>Hand on</ActionButton>}
          {/* BUGS.md TK51: a task parked already is not offered Park again (the API refuses a second park in words). */}
          {r.status === 'parked' || r.parkedUntil ? null
            : <ActionButton type="button" className={mode === 'park' ? 'opt on' : 'opt'} aria-expanded={mode === 'park'} onClick={() => setMode(mode === 'park' ? null : 'park')}>Park</ActionButton>}
          {r.status === 'accepted' ? <ActionButton type="button" className="opt" onClick={() => void onAct(r, 'start')}>Start it</ActionButton> : null}
          {asPage ? null : <ActionButton reading type="button" className="opt" onClick={() => go(`/tasks/${r.id}`)}>Open as a page</ActionButton>}
          </>}
        </div>
      ) : <p className="note">{r.ownerName ?? 'Someone else'}'s task: you may read it here; its owner changes it.</p>}

      {mode === 'assign' ? <AssignPick r={r} onCancel={() => setMode(null)} onPick={(p) => { setMode(null); void onAct(r, 'assign', { to: p.principalId }, assignedWords(p, true), 'withdraw'); }} /> : null}
      {mode === 'hand' ? <HandOn team={team} onDone={async (to, dueAt) => { setMode(null); await onAct(r, 'hand_on', { to, dueAt }, 'Handed on. It stays yours, with a timeline.'); }} onCancel={() => setMode(null)} /> : null}
      {mode === 'park' ? <ParkPick onPick={async (until) => { setMode(null); await onAct(r, 'park', { until }, 'Parked. It comes back on the day.'); }} onCancel={() => setMode(null)} /> : null}

      <div className="tk-grid">
        <div>
          <dl className="tk-fields">
            <dt>Due</dt>
            <dd>{writable ? <ActionInput type="date" aria-label="Due" value={inputDate(r.dueAt)} onChange={(e) => { const v = fromInputDate(e.target.value); if (v) void edit({ dueAt: v }); }} /> : dueWords(r.dueAt, r.duePrecision)}
              {d?.sources[0]?.quote ? <small> from “{d.sources[0].quote}”</small> : null}</dd>
            <dt>Priority</dt>
            <dd>{writable ? <ActionSelect aria-label="Priority" value={r.priority} onChange={(e) => void edit({ priority: Number(e.target.value) })}>{[1, 2, 3, 4].map((p) => <option key={p} value={p}>{priorityWords(p)}</option>)}</ActionSelect> : priorityWords(r.priority)}
              {/* Why Cobrain set it (TL4, TK48), while the figure is still Cobrain's: the newest reason in its activity. */}
              {r.prioritySource !== 'person' && whyPriority(d) ? <small className="tk-why">{whyPriority(d)}</small> : null}</dd>
            <dt>Filed under</dt>
            <dd>{where ? <>{where.area} › <ProjectName where={where} /></> : 'Not yet filed'}
              {r.filedBy === 'rule' || r.filedBy === 'model' ? <small> by Cobrain.</small> : null}
              {writable ? <> <ActionButton type="button" className="link inline" aria-expanded={mode === 'move'} onClick={() => setMode(mode === 'move' ? null : 'move')}>Move</ActionButton></> : null}</dd>
            <dt>Time needed</dt>
            <dd>{writable ? (
                  <ActionSelect aria-label="Time needed" value={r.minutesNeeded ?? ''} onChange={(e) => void edit({ minutesNeeded: e.target.value === '' ? null : Number(e.target.value) })}>
                    <option value="">Not set</option>{[15, 30, 45, 60, 90, 120, 180, 240].map((m) => <option key={m} value={m}>{minutesWords(m)}</option>)}
                  </ActionSelect>) : r.minutesNeeded ? minutesWords(r.minutesNeeded) : 'Not set'}
              <small> {r.booked ? `booked ${dueWords(r.booked.start, 'minute')}` : 'not booked'}</small></dd>
          </dl>
          {mode === 'move' ? <MovePick r={r} projects={projects} areas={areas} onMove={async (projectId) => { setMode(null); await edit({ projectId }); }} onCancel={() => setMode(null)} /> : null}
          <div className="tk-psec">
            <H>Notes</H>
            {writable ? <ActionTextarea aria-label="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => { if (notes !== (r.notes ?? '')) void edit({ notes: notes.trim() === '' ? null : notes }); }} placeholder="Anything to remember about this" />
                      : <p>{r.notes ?? 'None.'}</p>}
          </div>
          <div className="tk-psec">
            <H>Steps <small>{d ? `${d.steps.filter((s) => s.done).length} of ${d.steps.length}` : ''}</small></H>
            {d?.steps.map((s) => (
              <label key={s.id} className={s.done ? 'tk-step done' : 'tk-step'}>
                <ActionInput type="checkbox" checked={s.done} disabled={!writable} onChange={async () => { await api.setStep(store, r.id, s.id, { done: !s.done }); await refresh(); await reload(); }} />
                <span>{s.title}</span>
              </label>
            ))}
            {writable ? (
              <form className="tk-addstep" onSubmit={async (e) => { e.preventDefault(); if (!step.trim()) return; try { await api.addStep(store, r.id, step.trim()); setStep(''); await refresh(); await reload(); } catch (x) { setErr(`That did not go through: ${errorWords(x)}.`); } }}>
                <label className="sr-only" htmlFor={`step-${r.id}`}>Add a step</label>
                <ActionInput id={`step-${r.id}`} value={step} onChange={(e) => setStep(e.target.value)} placeholder="+ Add a step" />
              </form>
            ) : null}
          </div>
        </div>
        <div>
          <div className="tk-psec first">
            <H>Where this came from</H>
            {d && d.sources.length > 0 ? d.sources.slice(0, 2).map((s, i) => (
              <div key={i} className="quote">{s.quote ? `“${s.quote}”` : s.subject}<small>{[s.who, s.subject && s.quote ? s.subject : null, s.at ? dueWords(s.at, 'day') : null].filter(Boolean).join(', ')}</small></div>
            )) : <p className="note">{r.createdFrom === 'manual' ? 'You added it.' : 'Nothing on record behind it beyond the task itself.'}</p>}
            {/* BUGS.md TK32: the newest two are shown; the page says when there are more, never cuts them in silence. */}
            {d && d.sources.length > 0 && cutOf(Math.min(2, d.sources.length), d.sourcesTotal ?? d.sources.length) ? <p className="note tk-cut">{cutOf(Math.min(2, d.sources.length), d.sourcesTotal ?? d.sources.length)} sources shown, the newest.</p> : null}
          </div>
          <Conversation H={H} r={r} chatId={d?.chatId ?? null} onLinked={async () => { await refresh(); await reload(); }} />
          {d && d.activity.length > 0 ? (
            <div className="tk-psec">
              <H>Activity</H>
              <ul className="tk-activity">{d.activity.slice(0, 5).map((a, i) => <li key={i}>{activityWords(a)} <span>{dueWords(a.at, 'day')}</span></li>)}</ul>
              {cutOf(Math.min(5, d.activity.length), d.activityTotal ?? d.activity.length) ? <p className="note tk-cut">{cutOf(Math.min(5, d.activity.length), d.activityTotal ?? d.activity.length)} lines shown, the newest.</p> : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Cobrain's reason for a task's priority: its newest own line that says it set one (extract/priority.ts). */
function whyPriority(d: TaskDetail | null): string | null {
  return d?.activity.find((a) => a.kind === 'note' && a.actor === 'cobrain' && a.note?.startsWith('Cobrain set it at priority'))?.note ?? null;
}

/** Priority 1 is the most urgent (the schema's scale, 1 to 4). */
function priorityWords(p: number): string { return p === 1 ? 'P1, most urgent' : p === 4 ? 'P4, least urgent' : `P${p}`; }

const ACTIVITY: Record<string, string> = {
  created: 'added it', accepted: 'accepted it', owner_changed: 'gave it to someone', delegated: 'handed it on', activity: 'something happened',
  silence_warning: 'Cobrain warned it had gone quiet', parked: 'parked it', returned: 'it came back', scheduled: 'booked time', done: 'ticked it done',
  dismissed: 'said it was not a task', merged: 'merged it', note: 'changed it', priority_changed: 'changed the priority', due_changed: 'moved the date',
};
function activityWords(a: TaskDetail['activity'][number]): string {
  const who = a.actor === 'cobrain' ? 'Cobrain' : a.byMe ? 'You' : a.by ?? (a.actor === 'system' || a.actor === 'ingest' ? 'Cobrain' : 'Someone');
  if (a.kind === 'note' && a.note?.startsWith('status ')) return `${who} moved it to ${a.note.split('→ ')[1]?.replace('_', ' ') ?? 'a new state'}`;
  // Cobrain's own line says what it did in its own words, as its priority's reason (pipeline TL4, BUGS.md TK48).
  if (a.kind === 'note' && a.actor === 'cobrain' && a.note) return a.note;
  return `${who} ${ACTIVITY[a.kind] ?? 'changed it'}`;
}

function Conversation({ H, r, chatId, onLinked }: { H: 'h2' | 'h3'; r: Row; chatId: string | null; onLinked: () => Promise<void> }) {
  const [messages, setMessages] = useState<MessageView[]>([]);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const store = r.store.tenantId;
  const other = r.delegateId && r.mine ? { id: r.delegateId, name: r.delegateName } : !r.mine ? { id: r.ownerId, name: r.ownerName } : null;
  useEffect(() => {
    if (!chatId) { setMessages([]); return; }
    messenger.messages(store, chatId).then((m) => setMessages(m.messages.slice(-4))).catch(() => undefined);
  }, [store, chatId]);
  const sendIt = async () => {
    if (!text.trim()) return;
    setErr(null);
    try {
      let id = chatId;
      if (!id) {
        const made = await messenger.create(store, { kind: 'group', title: r.title.slice(0, 120), participantIds: other ? [other.id] : [] });
        id = made.conversation.id;
        await api.linkChat(store, r.id, id);
      }
      await messenger.send(store, id, { body: text.trim() });
      setText('');
      await onLinked();
      const m = await messenger.messages(store, id); setMessages(m.messages.slice(-4));
    } catch (e) { setErr(`That did not go through: ${errorWords(e)}.`); }
  };
  return (
    <div className="tk-psec">
      <H>Conversation <small>also in Chats</small></H>
      {messages.map((m) => <div key={m.id} className="tk-msg"><b>{m.mine ? 'You' : m.senderName}</b><small>{dueWords(m.sentAt, 'minute')}</small><p>{m.body}</p></div>)}
      <form className="tk-reply" onSubmit={(e) => { e.preventDefault(); void sendIt(); }}>
        <label className="sr-only" htmlFor={`reply-${r.id}`}>Write about this task</label>
        <ActionInput id={`reply-${r.id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder={other?.name ? `Write to ${other.name.split(' ')[0]} about this task…` : 'A note to yourself in this task\'s chat…'} />
        {text.trim() ? <ActionButton type="submit" className="opt small">Post</ActionButton> : null}
      </form>
      {err ? <p className="error" role="alert">{err}</p> : null}
    </div>
  );
}

/** First names, said as a list: "Conor", "Conor and Will", "Conor, Will and Tom". */
function namesWords(names: string[]): string {
  const first = names.map((n) => n.split(' ')[0] ?? n);
  return first.length <= 1 ? first.join('') : `${first.slice(0, -1).join(', ')} and ${first[first.length - 1]}`;
}

/** What the strip says once a task is put to someone (U298): it waits for them and stays the reader's till taken. */
function assignedWords(p: TeamPerson, proposal: boolean): string {
  const name = namesWords([p.name]);
  const lead = proposal ? 'Accepted and put to' : 'Put to';
  return p.signedIn
    ? `${lead} ${name}: it waits in their list and stays yours until they take it.`
    : `${lead} ${name}, who has not signed in yet: it waits in their list and stays yours until they take it.`;
}

/**
 * What sits where the owner would on a task's row (U298). A task of the reader's, open and with nobody: Assign and
 * Remove. One the reader has put to someone: who it waits for, and Withdraw. One put to the reader: Take it and Not me.
 * Anything else says whose it is, as it always has. Mark done is the tick; Book time, Hand on, Park and Start it stay
 * in the opened task.
 */
function RowChoices({ r, onAct, assigning, onAssign }: {
  r: Row; assigning: boolean; onAssign: () => void;
  onAct: (r: Row, a: TaskAct, extra?: Parameters<typeof api.act>[3], words?: string, undo?: TaskAct) => Promise<void>;
}) {
  const closed = r.status === 'done' || r.status === 'dismissed';
  if (r.askedBy && !closed) {
    const by = r.askedBy;
    return (
      <div className="opts">
        <ActionButton type="button" className="opt lead" onClick={() => void onAct(r, 'take', { requestId: by.requestId }, `Taken: ${r.title} is yours now.`)}>Take it</ActionButton>
        <ActionButton type="button" className="opt" onClick={() => void onAct(r, 'not_me', { requestId: by.requestId }, `Not you: ${namesWords([by.name])} is told in Cobrain.`)}>Not me</ActionButton>
      </div>
    );
  }
  if (r.mine && !closed && !r.delegateId && r.asked.length > 0) {
    const names = namesWords(r.asked.map((a) => a.name));
    return (
      <div className="opts waiting">
        <span className="tk-waiting">Waiting for {names}</span>
        <ActionButton type="button" className="opt" onClick={() => void onAct(r, 'withdraw', {}, `Withdrawn: it is no longer waiting for ${names}.`)}>Withdraw</ActionButton>
      </div>
    );
  }
  if (r.mine && !closed && !r.delegateId) {
    return (
      <div className="opts">
        {r.store.kind === 'personal' ? null
          : <ActionButton type="button" className={assigning ? 'opt on' : 'opt'} aria-expanded={assigning} onClick={onAssign}>Assign</ActionButton>}
        <ActionButton type="button" className="opt" onClick={() => void onAct(r, 'not_a_task', {}, `Removed: ${r.title}.`, 'restore')}>Remove</ActionButton>
      </div>
    );
  }
  return (
    <span className={r.delegateId ? 'person del' : 'person'}>
      {r.delegateId ? (r.mine ? `→ ${r.delegateName ?? 'someone'}` : `${r.ownerName ?? 'Someone'}'s, with you`) : r.mine ? 'You' : r.ownerName ?? ''}
      {r.delegateId && quietFor(r) !== null && r.mine ? <span className="q">quiet {r.quietDays} days</span> : null}
    </span>
  );
}

/**
 * Assign's picker (U298): everyone in the store but the reader, people only, as the server reads them for this task.
 * Who has not signed in yet is marked so, and the task waits in their list for their first sign-in; who the privacy
 * rails do not let read the task is offered greyed, with the reason, and the server refuses them too. Nothing is sent
 * to anyone, and nobody is invited by it (D69; module 05 R34).
 */
function AssignPick({ r, onPick, onCancel }: { r: Row; onPick: (p: AssignablePerson) => void; onCancel: () => void }) {
  const [people, setPeople] = useState<AssignablePerson[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api.people(r.store.tenantId, r.id).then((x) => { if (live) setPeople(x.people); }).catch((e) => { if (live) setErr(`Who can take it could not be read: ${errorWords(e)}.`); });
    return () => { live = false; };
  }, [r.store.tenantId, r.id]);
  return (
    <div className="tk-ctl tk-assign" role="group" aria-label={`Assign "${r.title}" to`}>
      {err ? <span className="error" role="alert">{err}</span>
        : people === null ? <span className="tk-ctl-note">Reading who can take it…</span>
        : people.length === 0 ? <span className="tk-ctl-note">There is nobody else in this store to assign it to.</span>
        : people.map((p) => {
            const why = !p.canRead ? 'cannot see this task' : p.asked ? 'already asked' : null;
            return (
              <ActionButton key={p.principalId} type="button" className="opt small tk-who" disabled={why !== null} onClick={() => onPick(p)}
                      title={!p.canRead ? 'The privacy rails do not let them read this task, so it cannot be put to them' : undefined}>
                <b>{p.name}</b>
                {p.signedIn ? null : <span>not signed in yet</span>}
                {why ? <span className="why">{why}</span> : null}
              </ActionButton>
            );
          })}
      <ActionButton reading type="button" className="opt small" onClick={onCancel}>Cancel</ActionButton>
      <span className="tk-ctl-note">It stays yours until they take it. Nothing is sent to them.</span>
    </div>
  );
}

function HandOn({ team, onDone, onCancel }: { team: TeamPerson[]; onDone: (to: string, dueAt: string) => Promise<void>; onCancel: () => void }) {
  const [to, setTo] = useState('');
  const [due, setDue] = useState('');
  if (team.length === 0) return <p className="note tk-inline">There is nobody else in this store to hand it to. <ActionButton reading type="button" className="link inline" onClick={onCancel}>Close</ActionButton></p>;
  return (
    <form className="tk-inline" onSubmit={(e) => { e.preventDefault(); const d = fromInputDate(due); if (to && d) void onDone(to, d); }}>
      <label>To <ActionSelect value={to} onChange={(e) => setTo(e.target.value)} required><option value="" disabled>Choose</option>{team.map((m) => <option key={m.principalId} value={m.principalId}>{m.signedIn ? m.name : `${m.name}, not signed in yet`}</option>)}</ActionSelect></label>
      <label>By <ActionInput type="date" value={due} onChange={(e) => setDue(e.target.value)} required /></label>
      <ActionButton type="submit" className="opt small" disabled={!to || !due}>Hand on</ActionButton>
      <ActionButton reading type="button" className="opt small" onClick={onCancel}>Cancel</ActionButton>
      <span className="note">It stays yours, with a timeline; silence before the date warns you.</span>
    </form>
  );
}

function ParkPick({ onPick, onCancel }: { onPick: (until: string) => Promise<void>; onCancel: () => void }) {
  const at = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(8, 0, 0, 0); return d.toISOString(); };
  const monday = () => { const d = new Date(); const n = ((8 - d.getDay()) % 7) || 7; return at(n); };
  return (
    <div className="tk-inline" role="group" aria-label="Park until">
      <ActionButton type="button" className="opt small" onClick={() => void onPick(at(1))}>Tomorrow</ActionButton>
      <ActionButton type="button" className="opt small" onClick={() => void onPick(monday())}>Monday</ActionButton>
      <ActionButton type="button" className="opt small" onClick={() => void onPick(at(7))}>In a week</ActionButton>
      <ActionButton reading type="button" className="opt small" onClick={onCancel}>Cancel</ActionButton>
    </div>
  );
}

function MovePick({ r, projects, areas, onMove, onCancel }: { r: Row; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>; onMove: (projectId: string | null) => Promise<void>; onCancel: () => void }) {
  const [area, setArea] = useState('');
  const [name, setName] = useState('');
  const mine = areas.filter((a) => a.store.tenantId === r.store.tenantId);
  const inArea = [...projects.values()].filter((p) => p.areaId === area);
  return (
    <form className="tk-inline" onSubmit={async (e) => {
      e.preventDefault();
      if (!area) return;
      if (name.trim()) { const made = await api.addProject(r.store.tenantId, area, name.trim()); await onMove(made.id); }
    }}>
      <label>Area <ActionSelect value={area} onChange={(e) => setArea(e.target.value)}><option value="" disabled>Choose</option>{mine.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</ActionSelect></label>
      {area ? (
        <>
          {inArea.map((p) => <ActionButton key={p.id} type="button" className="opt small" onClick={() => void onMove(p.id)}>{p.name}</ActionButton>)}
          <label className="sr-only" htmlFor={`newproj-${r.id}`}>A new project</label>
          <ActionInput id={`newproj-${r.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Or a new project" />
          {name.trim() ? <ActionButton type="submit" className="opt small">File it there</ActionButton> : null}
        </>
      ) : null}
      {r.projectId ? <ActionButton type="button" className="opt small" onClick={() => void onMove(null)}>Not yet filed</ActionButton> : null}
      <ActionButton reading type="button" className="opt small" onClick={onCancel}>Cancel</ActionButton>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Board (U47): by state; move a card with its own buttons or by dragging. Done is the person's tick.
// ---------------------------------------------------------------------------------------------------------------
function Board({ rows, projects, areas, onAct, go }: {
  rows: Row[]; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>;
  onAct: (r: Row, a: TaskAct, extra?: Parameters<typeof api.act>[3], words?: string, undo?: TaskAct) => Promise<void>; go: (path: string) => void;
}) {
  // A task asked of a colleague and not yet taken waits on them as a hand-on does (Mike, 7 Oct, U376; BUGS.md TK34).
  const waiting = (r: Row) => r.status === 'delegated' || (r.mine && r.asked.length > 0 && r.status !== 'done' && r.status !== 'parked');
  const lanes: Array<[string, string, (r: Row) => boolean]> = [
    ['todo', 'To do', (r) => (r.status === 'accepted' || r.status === 'scheduled') && !waiting(r)],
    ['doing', 'In progress', (r) => r.status === 'in_progress' && !waiting(r)],
    ['waiting', 'Waiting on someone', waiting],
    // A parked task is still the reader's and still counted, so it has a lane of its own (Mike, 7 Oct, U376; TK18, TK21).
    ['parked', 'Parked', (r) => r.status === 'parked'],
    ['done', 'Done', (r) => r.status === 'done'],
  ];
  const [drag, setDrag] = useState<string | null>(null);
  // The tick is the owner's (TK24): a task handed to the reader is ticked by its owner, so it is neither offered Mark
  // done nor dragged, as its row on the list says "Only its owner ticks it".
  const tickable = (r: Row) => r.mine || r.store.kind === 'personal';
  const moveTo = (r: Row, lane: string) => {
    if (!tickable(r)) return;
    if (lane === 'done' && r.status !== 'done') void onAct(r, 'done', {}, `Done: ${r.title}.`, 'undone');
    else if (lane === 'doing' && (r.status === 'accepted' || r.status === 'scheduled')) void onAct(r, 'start');
    else if (lane === 'todo' && r.status === 'in_progress') void onAct(r, 'to_do');
    else if (lane === 'todo' && r.status === 'done') void onAct(r, 'undone');
  };
  /** Who closed a done task (TK35): the reader's tick, someone else's, or Cobrain on what was said afterwards (D236). */
  const closedBy = (r: Row) => r.doneSource === 'cobrain' ? 'Cobrain closed it from the conversation'
    : r.doneByMe ? 'you ticked it' : `${r.doneBy ?? 'someone'} ticked it`;
  return (
    <div className="tk-board">
      {lanes.map(([k, label, test]) => {
        const list = rows.filter(test);
        const still = k === 'waiting' || k === 'parked';
        return (
          <section key={k} className="tk-lane" aria-label={label}
                   onDragOver={(e) => { if (!still) e.preventDefault(); }}
                   onDrop={(e) => { e.preventDefault(); const r = rows.find((x) => x.id === drag); setDrag(null); if (r && !still) moveTo(r, k); }}>
            <h2>{label} <small>{list.length}{k === 'done' ? ' this week' : ''}</small></h2>
            {k === 'waiting' && list.length === 0 ? <p className="note">Assign a task, or hand it on from its own page, to move it here.</p> : null}
            {list.map((r) => {
              const where = whereOf(r, projects, areas);
              const kind = dueKind(r.dueAt, r.duePrecision);
              const asked = r.mine && r.asked.length > 0 && !r.delegateId;
              const canMove = tickable(r) && !still;
              return (
                <div key={r.id} className={`tk-card${r.private ? ' pv' : ''}${r.status === 'done' ? ' done' : ''}`} data-task={r.id}
                     draggable={canMove} onDragStart={() => { if (canMove) setDrag(r.id); }}>
                  <div className="tk-card-h"><ActionButton reading type="button" className="tk-card-t" onClick={() => go(`/tasks/${r.id}`)}>{r.title}</ActionButton><span className={r.priority === 1 ? 'pri p1' : 'pri'} aria-label={`Priority ${r.priority}`} title={priorityWords(r.priority)}>P{r.priority}</span></div>
                  <div className="sub">
                    {where ? <span>{where.area} › <ProjectName where={where} /></span> : <span>Not yet filed</span>}
                    {r.status !== 'done' ? <span className={kind === 'overdue' || kind === 'today' ? 'plate' : 'plate quiet'}>{kind === 'overdue' || kind === 'today' ? <i aria-hidden="true" /> : null}{dueWords(r.dueAt, r.duePrecision)}</span>
                      : <span>{closedBy(r)}{r.doneAt ? `, ${dueWords(r.doneAt, 'day')}` : ''}</span>}
                    {r.delegateId && r.mine ? <span className="person">→ {r.delegateName}</span> : null}
                    {r.delegateId && !r.mine ? <span className="person">{r.ownerName ?? 'Someone'}'s, with you</span> : null}
                    {asked ? <span className="tk-waiting">asked {r.asked.map((a) => a.name).join(', ')}, not taken yet</span> : null}
                    {quietFor(r) !== null ? <span className="warnc">quiet {r.quietDays} days</span> : null}
                    {r.status === 'parked' || r.parkedUntil ? <span className="tk-parked">{r.parkedUntil ? `Parked till ${dueWords(r.parkedUntil, 'day')}` : 'Parked'}</span> : null}
                  </div>
                  <div className="tk-card-moves">
                    {k === 'todo' && tickable(r) ? <ActionButton type="button" className="opt small" onClick={() => moveTo(r, 'doing')}>Start it</ActionButton> : null}
                    {k === 'doing' && tickable(r) ? <ActionButton type="button" className="opt small" onClick={() => moveTo(r, 'todo')}>Back to do</ActionButton> : null}
                    {(k === 'todo' || k === 'doing') && tickable(r) ? <ActionButton type="button" className="opt small done-move" onClick={() => moveTo(r, 'done')}>Mark done</ActionButton> : null}
                    {k === 'waiting' && asked ? <ActionButton type="button" className="opt small" onClick={() => void onAct(r, 'withdraw', {}, `Withdrawn: it is no longer waiting for ${namesWords(r.asked.map((a) => a.name))}.`)}>Withdraw</ActionButton> : null}
                    {k === 'parked' && tickable(r) ? <ActionButton type="button" className="opt small" onClick={() => void onAct(r, 'unpark', {}, `Back: ${r.title} is in your list again.`)}>Bring it back now</ActionButton> : null}
                    {k === 'done' && tickable(r) ? <ActionButton type="button" className="opt small" onClick={() => void onAct(r, 'undone', {}, `Not done: ${r.title} is open again.`)}>Not done after all</ActionButton> : null}
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Calendar (U47): the week; tasks on their due day at the top; booked time dashed beside the person's meetings.
// ---------------------------------------------------------------------------------------------------------------
function Calendar({ rows, store }: { rows: Row[]; store: Store }) {
  const [offset, setOffset] = useState(0);
  // The week that holds today, Monday to Sunday (a review found a weekend view of next week hid what was due).
  const monday = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7); return d; }, [offset]);
  const days = Array.from({ length: 7 }, (_, i) => new Date(monday.getTime() + i * 86_400_000));
  const overdue = rows.filter((r) => r.status !== 'done' && dueKind(r.dueAt, r.duePrecision) === 'overdue');
  const [events, setEvents] = useState<CalEvent[]>([]);
  useEffect(() => { api.week(store.tenantId, monday.toISOString()).then((w) => setEvents(w.events)).catch(() => setEvents([])); }, [store.tenantId, monday]);
  const sameDay = (iso: string, d: Date) => { const x = new Date(iso); return x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth() && x.getDate() === d.getDate(); };
  const inWeek = (iso: string) => days.some((d) => sameDay(iso, d));
  // What sits in the grid this week: meetings, and time booked for a task (booked time stays as it is, U376).
  const timed: CalEvent[] = [
    ...events.filter((e) => !e.allDay && inWeek(e.start)),
    ...rows.filter((r) => r.booked && inWeek(r.booked.start)).map((r) => ({ id: `b:${r.id}`, title: r.title, start: r.booked!.start, end: r.booked!.end, allDay: false, booked: r })),
  ];
  // The hours stretch to the week's earliest and latest entry (TK45), never narrower than the working day.
  const hourOf = (iso: string) => { const d = new Date(iso); return d.getHours() + d.getMinutes() / 60; };
  const START = Math.max(0, Math.min(8, ...timed.map((e) => Math.floor(hourOf(e.start)))));
  const END = Math.min(24, Math.max(19, ...timed.map((e) => (sameDay(e.end, new Date(e.start)) ? Math.ceil(hourOf(e.end)) : 24))));
  const H = 44;
  const top = (iso: string) => Math.max(0, (hourOf(iso) - START) * H);
  const height = (a: string, b: string) => Math.min((END - START) * H - top(a), Math.max(18, ((new Date(b).getTime() - new Date(a).getTime()) / 3_600_000) * H));
  // Due row: a task with a time keeps its place there, said with its time first ("15:00 Call Conor"; Mike, U376).
  const dueLine = (r: Row) => r.duePrecision === 'minute' ? `${String(new Date(r.dueAt).getHours()).padStart(2, '0')}:${String(new Date(r.dueAt).getMinutes()).padStart(2, '0')} ${r.title}` : r.title;
  return (
    <div className="tk-cal-wrap">
      <div className="tk-filters">
        <div className="tk-chips">
          <ActionButton reading type="button" className="chip" onClick={() => setOffset(offset - 1)} aria-label="The week before">←</ActionButton>
          <ActionButton reading type="button" className={offset === 0 ? 'chip on' : 'chip'} onClick={() => setOffset(0)}>{offset === 0 ? 'This week' : offset === 1 ? 'Next week' : offset === -1 ? 'Last week' : 'Back to this week'}</ActionButton>
          <ActionButton reading type="button" className="chip" onClick={() => setOffset(offset + 1)} aria-label="The week after">→</ActionButton>
        </div>
        <span className="note">Tasks on their due day at the top; time booked for a task is dashed beside your meetings.</span>
      </div>
      {overdue.length > 0 ? <p className="tk-cal-over"><b>{overdue.length} overdue:</b> {overdue.map((r) => r.title).join('; ')}</p> : null}
      <div className="tk-cal" role="region" aria-label={`The week of ${dueWords(monday.toISOString(), 'day')}`}>
        <div className="hd" />
        {days.map((d) => <div key={d.toISOString()} className={sameDay(new Date().toISOString(), d) ? 'hd today' : 'hd'}>{d.toLocaleDateString('en-GB', { weekday: 'short' })} {d.getDate()}{sameDay(new Date().toISOString(), d) ? <span className="sr-only"> (today)</span> : null}</div>)}
        <div className="due-l">Due</div>
        {days.map((d) => (
          <div key={d.toISOString()} className="due">
            {rows.filter((r) => r.status !== 'done' && sameDay(r.dueAt, d)).sort((a, b) => a.dueAt.localeCompare(b.dueAt))
              .map((r) => <div key={r.id} className={r.private ? 'pv' : ''} title={dueLine(r)}>{dueLine(r)}</div>)}
          </div>
        ))}
        <div className="hours">{Array.from({ length: END - START }, (_, i) => <div key={i} className="hr">{String(START + i).padStart(2, '0')}:00</div>)}</div>
        {days.map((d) => (
          <div key={d.toISOString()} className="col" style={{ height: (END - START) * H }}>
            {sideBySide(timed.filter((e) => sameDay(e.start, d))).map(({ e, lane, lanes }) => {
              const style = { top: top(e.start), height: height(e.start, e.end), left: `${(lane / lanes) * 100}%`, width: `${100 / lanes}%` };
              return e.booked
                ? <div key={e.id} className="ev b" style={style}>{e.title}<small>booked, {minutesWords(Math.round((new Date(e.end).getTime() - new Date(e.start).getTime()) / 60000))}</small></div>
                : <div key={e.id} className="ev m" style={style}>{e.title}</div>;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

interface CalEvent { id: string; title: string; start: string; end: string; allDay: boolean; booked?: Row }

/**
 * A day's meetings and booked time placed side by side where they overlap (TK44): each run of entries that overlap one
 * another shares the column, every entry in the run taking the first lane free when it starts, and the run's width
 * divided by the lanes it needed. An entry that overlaps nothing keeps the whole column.
 */
export function sideBySide<E extends { start: string; end: string }>(list: readonly E[]): Array<{ e: E; lane: number; lanes: number }> {
  const ms = (iso: string) => new Date(iso).getTime();
  const sorted = [...list].sort((a, b) => ms(a.start) - ms(b.start) || ms(b.end) - ms(a.end));
  const out: Array<{ e: E; lane: number; lanes: number }> = [];
  let run: Array<{ e: E; lane: number; lanes: number }> = [];
  let runEnd = -Infinity;
  const close = () => { const n = Math.max(1, ...run.map((x) => x.lane + 1)); for (const x of run) x.lanes = n; out.push(...run); run = []; };
  for (const e of sorted) {
    if (ms(e.start) >= runEnd) { close(); runEnd = -Infinity; }
    // The first lane whose last entry has ended by the time this one starts.
    const busy = new Set(run.filter((x) => ms(x.e.end) > ms(e.start)).map((x) => x.lane));
    let lane = 0;
    while (busy.has(lane)) lane++;
    run.push({ e, lane, lanes: 1 });
    runEnd = Math.max(runEnd, ms(e.end));
  }
  close();
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Team (U47): everyone's tasks the rails let the person read, by person; hand-ons from the person marked.
// ---------------------------------------------------------------------------------------------------------------
function Team({ rows, projects, areas, me, team, go }: {
  rows: Row[]; projects: Map<string, ProjectView & { store: Store }>; areas: Array<AreaView & { store: Store }>; me: string; team: TeamPerson[]; go: (path: string) => void;
}) {
  const live = rows.filter((r) => r.status !== 'done' && r.store.kind === 'commercial');
  type Why = 'own' | 'handed' | 'asked';
  const people = new Map<string, { name: string; list: Array<{ r: Row; why: Why }> }>();
  const put = (key: string, name: string, r: Row, why: Why) => { const p = people.get(key) ?? { name, list: [] }; p.list.push({ r, why }); people.set(key, p); };
  people.set(me, { name: 'You', list: [] });   // the reader first, always
  for (const r of live) {
    // A task of the reader's put to a colleague sits under that colleague until they take it (TK34), as a hand-on does.
    if (r.ownerId === me && r.delegateId) put(r.delegateId, r.delegateName ?? 'Someone', r, 'handed');
    else if (r.ownerId === me && r.asked.length > 0) put(r.asked[0]!.principalId, r.asked[0]!.name, r, 'asked');
    else put(r.ownerId, r.ownerId === me ? 'You' : r.ownerName ?? 'Someone', r, 'own');
  }
  for (const p of people.values()) p.list.sort((a, b) => a.r.dueAt.localeCompare(b.r.dueAt) || a.r.priority - b.r.priority);
  const signedIn = new Map(team.map((t) => [t.principalId, t.signedIn]));
  const groups = [...people.entries()].filter(([id, p]) => id === me || p.list.length > 0);
  // Everyone else in the store is named once, in one line, never as an empty heading each (U376).
  const rest = team.filter((t) => !people.has(t.principalId));
  const restOut = rest.filter((t) => !t.signedIn).map((t) => t.name);
  const restIn = rest.filter((t) => t.signedIn).map((t) => t.name);
  const list = (names: string[]) => names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return (
    <div className="tk-tbl team">
      {groups.map(([id, p]) => {
        const own = p.list.filter((x) => x.why === 'own');
        const handed = p.list.filter((x) => x.why === 'handed');
        const asked = p.list.filter((x) => x.why === 'asked');
        const overdue = p.list.filter((x) => dueKind(x.r.dueAt, x.r.duePrecision) === 'overdue').length;
        const quiet = handed.filter((x) => quietFor(x.r) !== null);
        return (
          <Fragment key={id}>
            <div className="tk-teamh"><h2>{p.name}</h2>
              {own.length || id === me ? <span>{own.length} open</span> : null}
              {overdue ? <span className="warnc">{overdue} overdue</span> : null}
              {handed.length ? <span>{handed.length} handed on by you{quiet.length ? <span className="warnc">, {quiet.length === 1 ? `quiet ${quiet[0]!.r.quietDays} days` : `${quiet.length} quiet`}</span> : null}</span> : null}
              {asked.length ? <span>{asked.length} asked by you</span> : null}
              {id !== me && signedIn.get(id) === false ? <span>not signed in yet</span> : null}
            </div>
            {id === me && p.list.length === 0 ? <p className="tk-none">Nothing of yours is open.</p> : null}
            {p.list.map(({ r, why }) => {
              const where = whereOf(r, projects, areas);
              const kind = dueKind(r.dueAt, r.duePrecision);
              return (
                <ActionButton reading key={r.id} type="button" className="tk-row team" data-task={r.id} onClick={() => go(`/tasks/${r.id}`)}>
                  <span className="t"><span className="tt">{r.title}</span></span>
                  <span className="where">{where ? <><b>{where.area}</b> › {where.project}</> : 'Not yet filed'}</span>
                  <span className={kind === 'overdue' || kind === 'today' ? 'plate' : 'plate quiet'}>{kind === 'overdue' || kind === 'today' ? <i aria-hidden="true" /> : null}{dueWords(r.dueAt, r.duePrecision)}</span>
                  <span className="person">{why === 'handed' ? 'from you' : why === 'asked' ? 'asked by you, not taken yet' : ''}</span>
                  <span className={r.priority === 1 ? 'pri p1' : 'pri'} title={priorityWords(r.priority)}>P{r.priority}</span>
                </ActionButton>
              );
            })}
          </Fragment>
        );
      })}
      {rest.length > 0 ? (
        <>
          <div className="tk-teamh"><h2>Everyone else</h2><span>nothing you may see</span></div>
          {restOut.length ? <p className="tk-none">{list(restOut)} {restOut.length === 1 ? 'has' : 'have'} not signed in yet, so they hold no tasks here.</p> : null}
          {restIn.length ? <p className="tk-none">{list(restIn)} {restIn.length === 1 ? 'holds' : 'hold'} nothing you may see.</p> : null}
        </>
      ) : null}
    </div>
  );
}

export function TasksHeading({ children }: { children: ReactNode }) { return <div className="tk-top">{children}</div>; }
